// src/components/BookCover/TextStyleToolbar.jsx
import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Rect, Circle, Triangle, Polygon, Line, FabricImage, Shadow, PencilBrush, CircleBrush, SprayBrush, Group } from 'fabric';
import { useToast } from '../../context/ToastContext';
import { RoughBrush } from './customBrushes';
import '../../styles/TextStyleToolbar.css';
import { FONT_NAMES as FONTS } from '../../config/fonts';

const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48, 56, 64, 72, 96];

const PALETTE = [
  '#000000', '#333333', '#666666', '#999999', '#cccccc', '#ffffff',
  '#ff0000', '#e74c3c', '#ff9900', '#f39c12', '#ffff00', '#f1c40f',
  '#00ff00', '#2ecc71', '#00ffff', '#1abc9c', '#0000ff', '#3498db',
  '#9900ff', '#9b59b6', '#ff00ff', '#e91e63', '#8B4513', '#D2691E',
  '#F4A460', '#2F4F4F', '#708090', '#B8860B', '#1a1a2e', '#16213e'
];

const STROKE_STYLES = [
  { key: 'smooth',            label: 'Smooth' },
  { key: 'gouache',           label: 'Gouache' },
  { key: 'oil-paint',         label: 'Oil Paint' },
  { key: 'oil-paint-flat',    label: 'Oil Paint Flat' },
  { key: 'color-change',      label: 'Color Change' },
  { key: 'bit-husky',         label: 'Bit Husky' },
  { key: 'rough',             label: 'Rough' },
  { key: 'lighter-ink',       label: 'Lighter Ink' },
  { key: 'darker-bleed',      label: 'Darker Bleed' },
  { key: 'light-running-ink', label: 'Light Running Ink' },
];

// Object border (stroke) style options — dash pattern scales with stroke width so
// thicker borders get proportionally longer dashes, matching Canva's behavior.
const BORDER_STYLE_OPTIONS = [
  { key: 'none',       label: 'None' },
  { key: 'solid',      label: 'Solid' },
  { key: 'dashed',     label: 'Dashed' },
  { key: 'fineDashed', label: 'Fine Dashed' },
  { key: 'dotted',     label: 'Dotted' },
];

const getBorderDashArray = (style, width) => {
  const w = Math.max(1, width || 1);
  switch (style) {
    case 'dashed':     return [w * 3, w * 2];
    case 'fineDashed': return [w * 1.2, w];
    case 'dotted':     return [0.001, w * 2];
    default:           return null; // 'none' / 'solid'
  }
};

const getBorderLineCap = (style) => (style === 'dotted' ? 'round' : 'butt');

// Fixed-scale dash previews for the style-picker icon buttons — independent of the
// object's actual stroke width, so each button always renders a consistent, clearly
// distinct preview instead of relying on hard-to-tell-apart dash-character glyphs.
const BORDER_ICON_DASH = {
  dashed: '6,4',
  fineDashed: '2.5,2.5',
  dotted: '0.1,4',
};

// Pre-seeded jitter offsets so the Rough / Bit Husky previews look the same every render
const PREVIEW_JITTER = Array.from({ length: 50 }, (_, i) => ({
  x: Math.sin(i * 2.3) * 2.5,
  y: Math.cos(i * 1.7) * 2.5,
}));
const SPRAY_DOTS = Array.from({ length: 120 }, (_, i) => ({
  ox: Math.sin(i * 5.1) * 7,
  oy: Math.cos(i * 3.9) * 7,
  r:  0.5 + Math.abs(Math.sin(i * 7.3)),
  a:  0.4 + 0.5 * Math.abs(Math.cos(i * 2.7)),
}));

const drawStrokePreview = (ctx, style, w, h) => {
  ctx.clearRect(0, 0, w, h);

  // Wavy path across the preview width
  const pts = Array.from({ length: 25 }, (_, i) => {
    const t = i / 24;
    return { x: 6 + t * (w - 12), y: h / 2 + Math.sin(t * Math.PI * 2) * (h * 0.27) };
  });

  const stroke = (lineWidth, color, lineCap = 'round', shadow = null) => {
    ctx.save();
    if (shadow) {
      ctx.shadowColor  = shadow.color;
      ctx.shadowBlur   = shadow.blur;
      ctx.shadowOffsetX = shadow.ox || 0;
      ctx.shadowOffsetY = shadow.oy || 0;
    }
    ctx.beginPath();
    ctx.lineWidth  = lineWidth;
    ctx.strokeStyle = color;
    ctx.lineCap    = lineCap;
    ctx.lineJoin   = 'round';
    ctx.moveTo(pts[0].x, pts[0].y);
    pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
    ctx.stroke();
    ctx.restore();
  };

  switch (style) {
    case 'smooth':
      stroke(1.5, '#1a1a1a');
      break;
    case 'gouache':
      stroke(3.5, 'rgba(20,20,20,0.92)', 'round', { blur: 1, color: 'rgba(20,20,20,0.3)' });
      break;
    case 'oil-paint':
      stroke(2.5, 'rgba(20,20,20,0.85)', 'round', { blur: 3, color: 'rgba(20,20,20,0.5)' });
      break;
    case 'oil-paint-flat':
      stroke(5, '#1a1a1a', 'square');
      break;
    case 'color-change':
      stroke(2, '#3355cc', 'round', { blur: 4, ox: 0.8, oy: 0.8, color: 'rgba(180,40,210,0.65)' });
      break;
    case 'bit-husky': {
      ctx.save();
      SPRAY_DOTS.forEach(({ ox, oy, r, a }, i) => {
        const pt = pts[Math.floor(i / (SPRAY_DOTS.length / pts.length)) % pts.length];
        ctx.fillStyle = `rgba(20,20,20,${a})`;
        ctx.beginPath();
        ctx.arc(pt.x + ox, pt.y + oy, r, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();
      break;
    }
    case 'rough': {
      ctx.save();
      ctx.beginPath();
      ctx.lineWidth  = 1.5;
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineCap    = 'round';
      ctx.lineJoin   = 'round';
      ctx.moveTo(pts[0].x, pts[0].y);
      pts.slice(1).forEach((p, i) => {
        const j = PREVIEW_JITTER[i % PREVIEW_JITTER.length];
        ctx.lineTo(p.x + j.x, p.y + j.y);
      });
      ctx.stroke();
      ctx.restore();
      break;
    }
    case 'lighter-ink':
      stroke(1.2, 'rgba(20,20,20,0.35)');
      break;
    case 'darker-bleed':
      stroke(2.5, 'rgba(10,10,10,0.95)', 'round', { blur: 6, color: 'rgba(10,10,10,0.7)' });
      break;
    case 'light-running-ink':
      stroke(1, 'rgba(20,20,20,0.22)');
      break;
    default:
      stroke(1.5, '#1a1a1a');
  }
};

const StrokePreviewCanvas = React.memo(({ strokeKey }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    drawStrokePreview(el.getContext('2d'), strokeKey, 80, 22);
  }, [strokeKey]);
  return (
    <canvas
      ref={ref}
      width={80}
      height={22}
      style={{ display: 'block', borderRadius: 2, background: '#f8f8f8' }}
    />
  );
});

// Book sizes — all at exactly 66 px/inch. realW/realH in inches for ruler conversion.
// Must match BOOK_SIZE_DIMS in BookCoverDesigner.jsx, useCanvasSetup.js, and renderFrontCoverThumbnail.js.
const BOOK_SIZES = [
  { key: 'half_letter', label: '5.5″ × 8.5″ (Half Letter)',   w: 363, h: 561, realW: 5.5,   realH: 8.5   },
  { key: 'trade_6x9',  label: '6″ × 9″ (Trade)',              w: 396, h: 594, realW: 6,     realH: 9     },
  { key: 'a5',         label: '5.83″ × 8.27″ (A5)',           w: 385, h: 546, realW: 5.83,  realH: 8.27  },
  { key: 'pocket_4x7', label: '4.25″ × 6.875″ (Pocket)',      w: 281, h: 454, realW: 4.25,  realH: 6.875 },
  { key: 'us_letter',  label: '8.5″ × 11″ (US Letter)',       w: 561, h: 726, realW: 8.5,   realH: 11    },
];

const TextStyleToolbar = forwardRef(function TextStyleToolbar({
  canvas,
  onAddText,
  fontSize,
  setFontSize,
  fontFamily,
  setFontFamily,
  textColor,
  setTextColor,
  bgColor,
  setBgColor,
  textAlign,
  setTextAlign,
  onCanvasChange,  // called when background or undo/redo changes canvas without firing object:* events
  onUploadImageAsset, // (dataUrl) => Promise<string|null> — uploads image to S3, returns stable src URL (null on failure)
  onSetFaceBackground, // (color) => void — sets bg for the currently active face
  faceBackgroundColor, // current face bg color to display in swatch
  onRemoveFaceClip,    // () => void — removes clipPath, making object span all faces
  onSetObjectFace,     // (face: 'all'|'front'|'spine'|'back') => void
  activePanel,         // 'cover' | 'text' | 'object' | 'insert' — controls which sections to show
  // -- Cover-tab props --
  onAddISBN,           // () => void
  onRemoveISBN,        // () => void
  isbnOnCanvas,        // boolean
  onAddBlurb,          // () => void
  draftBlurb,          // string
  activeView,          // 'front' | 'back' | 'full' | etc.
  faceDims,            // { faceW, faceH, spineW }
  onResizeCover,       // ({ faceW, faceH, spineW }) => void
  coverMargins,        // { top, right, bottom, left }
  onSetMargins,        // ({ top, right, bottom, left }) => void
  bookSize,            // current book size key e.g. 'trade_6x9'
  onBookSizeChange,    // (key, { faceW, faceH }) => void
  onDownloadPDF,       // () => void — opens the PDF export modal
  onUndoRedo,          // () => void — called after undo/redo so parent can re-sync derived state
  isHydrating,         // boolean — true while canvas is loading; blocks history saves during that window
  thicknessMode: thicknessModeProp,   // 'pages' | 'manual' — controlled by parent when in cover panel
  onThicknessModeChange,              // (mode) => void
}, ref) {
  const toast = useToast();
  const [activeText, setActiveText] = useState(null);
  const [activeObject, setActiveObject] = useState(null);
  const [activeTextAngle, setActiveTextAngle] = useState(0);
  const [textStyles, setTextStyles] = useState({ bold: false, italic: false, underline: false });
  const [showTextPalette, setShowTextPalette] = useState(false);
  const [showBgPalette, setShowBgPalette] = useState(false);
  const [showShapesMenu, setShowShapesMenu] = useState(false);
  const [showShapePalette, setShowShapePalette] = useState(false);
  const [showCanvasBgPalette, setShowCanvasBgPalette] = useState(false);
  const [showStrokePalette, setShowStrokePalette] = useState(false);
  const [showTbFillPalette, setShowTbFillPalette] = useState(false);
  const [showTbStrokePalette, setShowTbStrokePalette] = useState(false);
  const [showShadowPanel, setShowShadowPanel] = useState(false);
  const [showBorderPanel, setShowBorderPanel] = useState(false);
  const [showShadePalette, setShowShadePalette] = useState(false);
  const [localOpacity, setLocalOpacity] = useState(100);
  const [shadowState, setShadowState] = useState({ on: false, blur: 10, offsetX: 4, offsetY: 4, color: '#000000' });
  const [borderState, setBorderState] = useState({ width: 0, style: 'solid' });
  const [showShadowColorGrid, setShowShadowColorGrid] = useState(false);
  const [isObjectLocked, setIsObjectLocked] = useState(false);
  const [borderSizePct, setBorderSizePct] = useState(90);
  const [borderSizeDraft, setBorderSizeDraft] = useState('90');

  // -- Text-tab effect state --
  const [showTextShadowPanel, setShowTextShadowPanel] = useState(false);
  const [showTextShadowColorGrid, setShowTextShadowColorGrid] = useState(false);
  const [textShadowState, setTextShadowState] = useState({ on: false, blur: 10, offsetX: 4, offsetY: 4, color: '#000000' });
  const [showGlowPanel, setShowGlowPanel] = useState(false);
  const [showGlowColorGrid, setShowGlowColorGrid] = useState(false);
  const [glowState, setGlowState] = useState({ on: false, thickness: 10, color: '#ffff00', brightness: 0 });
  const [showReflectionPanel, setShowReflectionPanel] = useState(false);
  const [showReflectionColorGrid, setShowReflectionColorGrid] = useState(false);
  const [reflectionState, setReflectionState] = useState({ on: false, color: '', brightness: 0, amount: 50, fade: false, size: 100, gap: 0, blur: 0, direction: 'below', blendMode: 'source-over', rainbow: false, gradientColor1: '', gradientColor2: '', glitch: 0, wave: 0, waveFreq: 3 });
  const [showTextShadePanel, setShowTextShadePanel] = useState(false);
  const [textShadeAmount, setTextShadeAmount] = useState(0);
  const [showFontDropdown, setShowFontDropdown] = useState(false);

  // -- Draw-tab local state --
  const [drawTool, setDrawTool] = useState(null);   // null | 'pencil' | 'paint' | 'marker' | 'erase'
  const [eraseMode, setEraseMode] = useState('stroke'); // 'stroke' (whole line) | 'area' (partial)
  const [drawWidth, setDrawWidth] = useState(3);
  const [drawColor, setDrawColor] = useState('#000000');
  const [drawOpacity, setDrawOpacity] = useState(100);
  const [strokeStyle, setStrokeStyle] = useState('smooth');
  const [showStrokeDropdown, setShowStrokeDropdown] = useState(false);
  const [showDrawColorGrid, setShowDrawColorGrid] = useState(false);

  // -- Cover-tab local state --
  const [localFaceW, setLocalFaceW] = useState(360);
  const [localFaceH, setLocalFaceH] = useState(540);
  const [localSpineW, setLocalSpineW] = useState(100);
  // thicknessMode is controlled by the parent (BookCoverDesigner) so it persists
  // across sessions. Fall back to 'pages' when used outside the cover panel.
  const thicknessMode = thicknessModeProp ?? 'pages';
  const setThicknessMode = (mode) => onThicknessModeChange?.(mode);
  const [pageCount, setPageCount] = useState(200);

  // Draft strings for thickness inputs only (W/H are now a select)
  const [pageCountDraft, setPageCountDraft] = useState('200');
  const [spineWDraft, setSpineWDraft] = useState('100');

  // Draft strings for margin inputs
  const [marginDrafts, setMarginDrafts] = useState({ top: '0', right: '0', bottom: '0', left: '0' });

  // Keep margin drafts in sync when coverMargins prop changes externally
  useEffect(() => {
    if (coverMargins) {
      setMarginDrafts({
        top:    String(coverMargins.top    ?? 0),
        right:  String(coverMargins.right  ?? 0),
        bottom: String(coverMargins.bottom ?? 0),
        left:   String(coverMargins.left   ?? 0),
      });
    }
  }, [coverMargins]);

  // Sync local dims when faceDims prop changes
  useEffect(() => {
    if (faceDims) {
      setLocalFaceW(faceDims.faceW);
      setLocalFaceH(faceDims.faceH);
      setLocalSpineW(faceDims.spineW);
      setSpineWDraft(String(faceDims.spineW));
      // Use the same PX_PER_PAGE = 0.15 calibration as pagesToSpine/spineToPages
      const derivedPages = Math.round(faceDims.spineW / 0.15);
      setPageCountDraft(String(derivedPages));
      setPageCount(derivedPages);
    }
  }, [faceDims]);

  // -- Spine width formula based on KDP/IngramSpark industry standards -----------
  // Canvas is exactly 66 px/inch for all book sizes (396px = 6in for trade_6x9).
  // Standard white 60lb: pages × 0.002252 in × 66 px/in ≈ pages × 0.1486
  // Cream offset 60lb:   pages × 0.0025  in × 66 px/in ≈ pages × 0.165
  // We use 0.15 (white paper default). Minimum 12px (~0.18in, saddle-stitch floor).
  // Cap at 80% of faceW so the spine can never be wider than the cover.
  const PX_PER_PAGE = 0.15; // px per page — calibrated to 66 px/inch
  const pagesToSpine = (pages) =>
    Math.min(Math.round(localFaceW * 0.8), Math.max(12, Math.round(pages * PX_PER_PAGE)));
  const spineToPages = (sw) => Math.round(sw / PX_PER_PAGE);

  const commitPageCount = () => {
    const p = Math.max(24, Math.min(1200, parseInt(pageCountDraft) || pageCount));
    const sw = pagesToSpine(p);
    setPageCount(p); setPageCountDraft(String(p));
    setLocalSpineW(sw); setSpineWDraft(String(sw));
    onResizeCover?.({ faceW: localFaceW, faceH: localFaceH, spineW: sw });
  };
  const commitSpineW = () => {
    const sw = Math.max(12, Math.min(300, parseInt(spineWDraft) || localSpineW));
    const p = spineToPages(sw);
    setLocalSpineW(sw); setSpineWDraft(String(sw));
    setPageCount(p); setPageCountDraft(String(p));
    onResizeCover?.({ faceW: localFaceW, faceH: localFaceH, spineW: sw });
  };

  // Prevents the blur event that fires after Enter from triggering a second commit
  const skipNextBlurRef = useRef(false);
  
  // History for undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedo = useRef(false);
  const historyRef = useRef({ history: [], index: -1 });
  // Ref mirror of isHydrating so saveState (captured in a useEffect closure) always
  // sees the latest value without needing to re-register all canvas event listeners.
  const isHydratingRef = useRef(!!isHydrating);
  useEffect(() => { isHydratingRef.current = !!isHydrating; }, [isHydrating]);

  // Image upload ref
  const imageInputRef = useRef(null);

  // Close all dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't close if clicking inside dropdown menu or wrapper
      if (!e.target.closest('.dropdown-wrapper') && !e.target.closest('.dropdown-menu')) {
        setShowShapesMenu(false);
        setShowShapePalette(false);
        setShowCanvasBgPalette(false);
        setShowTextPalette(false);
        setShowBgPalette(false);
        setShowStrokePalette(false);
        setShowTbFillPalette(false);
        setShowTbStrokePalette(false);
        setShowShadowPanel(false);
        setShowShadePalette(false);
        setShowShadowColorGrid(false);
        setShowDrawColorGrid(false);
        setShowStrokeDropdown(false);
        setShowTextShadowPanel(false);
        setShowTextShadowColorGrid(false);
        setShowGlowPanel(false);
        setShowGlowColorGrid(false);
        setShowReflectionPanel(false);
        setShowReflectionColorGrid(false);
        setShowTextShadePanel(false);
        setShowFontDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Helper: read bold/italic/underline/font/size/color from object or current selection
  const refreshTextStyles = (tb) => {
    if (!tb) { setTextStyles({ bold: false, italic: false, underline: false }); return; }
    let bold, italic, underline;
    if (tb.isEditing && tb.selectionStart !== tb.selectionEnd) {
      // Characters are highlighted — check styles across all selected chars
      const start = tb.selectionStart;
      const end   = tb.selectionEnd;
      const allStyles = tb.getSelectionStyles(start, end) || [];
      const s0 = allStyles[0] || {};
      bold      = (s0.fontWeight  ?? tb.fontWeight)  === 'bold';
      italic    = (s0.fontStyle   ?? tb.fontStyle)   === 'italic';
      underline = !!(s0.underline  ?? tb.underline);

      // Font family — blank if mixed across selection
      const fonts  = allStyles.map(s => s.fontFamily ?? tb.fontFamily);
      const sizes  = allStyles.map(s => s.fontSize   ?? tb.fontSize);
      const colors = allStyles.map(s => s.fill       ?? tb.fill);
      setFontFamily(fonts.every(f => f === fonts[0])   ? fonts[0]  : '');
      setFontSize(  sizes.every(s => s === sizes[0])   ? sizes[0]  : '');
      setTextColor( colors.every(c => c === colors[0]) ? colors[0] : '');
    } else {
      bold      = tb.fontWeight  === 'bold';
      italic    = tb.fontStyle   === 'italic';
      underline = !!tb.underline;
      // No active highlight — show object-level properties
      setFontFamily(tb.fontFamily ?? 'Arial');
      setFontSize(  tb.fontSize   ?? 18);
      setTextColor( tb.fill       ?? '#000000');
    }
    setTextStyles({ bold, italic, underline });
  };

  // 🔁 Track active object for styling
  useEffect(() => {
    if (!canvas) return;

    const handleSelection = () => {
      const active = canvas.getActiveObject();
      setActiveObject(active);
      setLocalOpacity(Math.round((active?.opacity ?? 1) * 100));
      setBorderState({ width: active?.strokeWidth ?? 0, style: active?._borderStyle || 'solid' });
      setIsObjectLocked(!!active?._locked);
      if (active?.shadow) {
        const s = active.shadow;
        setShadowState({ on: true, blur: s.blur ?? 10, offsetX: s.offsetX ?? 4, offsetY: s.offsetY ?? 4, color: s.color || '#000000' });
      } else {
        setShadowState(prev => ({ ...prev, on: false }));
      }
      if (active && active.type === 'textbox') {
        setActiveText(active);
        setActiveTextAngle(active.angle || 0);
        refreshTextStyles(active);
        // Sync text-effect states from active textbox
        if (active.shadow && active._effectType === 'glow') {
          setGlowState({ on: true, thickness: active.shadow.blur ?? 10, color: active.shadow.color || '#ffff00', brightness: active._glowBrightness ?? 0 });
          setTextShadowState(prev => ({ ...prev, on: false }));
        } else if (active.shadow) {
          const s = active.shadow;
          setTextShadowState({ on: true, blur: s.blur ?? 10, offsetX: s.offsetX ?? 4, offsetY: s.offsetY ?? 4, color: s.color || '#000000' });
          setGlowState(prev => ({ ...prev, on: false }));
        } else {
          setTextShadowState(prev => ({ ...prev, on: false }));
          setGlowState(prev => ({ ...prev, on: false }));
        }
        const r = active._reflection;
        setReflectionState(r
          ? { on: !!r.on, color: r.color ?? '', brightness: r.brightness ?? 0, amount: r.amount ?? 50, fade: r.fade ?? false, size: r.size ?? 100, gap: r.gap ?? 0, blur: r.blur ?? 0, direction: r.direction ?? 'below', blendMode: r.blendMode ?? 'source-over', rainbow: r.rainbow ?? false, gradientColor1: r.gradientColor1 ?? '', gradientColor2: r.gradientColor2 ?? '', glitch: r.glitch ?? 0, wave: r.wave ?? 0, waveFreq: r.waveFreq ?? 3 }
          : { on: false, color: '', brightness: 0, amount: 50, fade: false, size: 100, gap: 0, blur: 0, direction: 'below', blendMode: 'source-over', rainbow: false, gradientColor1: '', gradientColor2: '', glitch: 0, wave: 0, waveFreq: 3 });
        setTextShadeAmount(active._shadeAmount ?? 0);
      } else {
        setActiveText(null);
        setActiveTextAngle(0);
        refreshTextStyles(null);
      }
    };

    // Fires as cursor moves or selection changes inside an editing textbox
    const handleTextSelectionChanged = () => {
      const active = canvas.getActiveObject();
      if (active && active.type === 'textbox') refreshTextStyles(active);
    };

    canvas.on('selection:created', handleSelection);
    canvas.on('selection:updated', handleSelection);
    canvas.on('selection:cleared', () => {
      setActiveText(null);
      setActiveObject(null);
      setActiveTextAngle(0);
      setLocalOpacity(100);
      setBorderState({ width: 0, style: 'solid' });
      setIsObjectLocked(false);
      setShadowState({ on: false, blur: 10, offsetX: 4, offsetY: 4, color: '#000000' });
      refreshTextStyles(null);
    });
    canvas.on('text:selection:changed', handleTextSelectionChanged);

    return () => {
      canvas.off('selection:created', handleSelection);
      canvas.off('selection:updated', handleSelection);
      canvas.off('selection:cleared');
      canvas.off('text:selection:changed', handleTextSelectionChanged);
    };
  }, [canvas]);

  // Save state for undo/redo
  const saveState = () => {
    if (!canvas || isUndoRedo.current || isHydratingRef.current) return;

    const json = JSON.stringify(canvas.toJSON(['name', '_locked', '_collabId', '_borderStyle']));
    if (json === historyRef.current.history[historyRef.current.index]) return;
    const newHistory = historyRef.current.history.slice(0, historyRef.current.index + 1);
    newHistory.push(json);

    // Keep only last 30 states
    if (newHistory.length > 30) newHistory.shift();

    historyRef.current.history = newHistory;
    historyRef.current.index = newHistory.length - 1;

    setHistory([...newHistory]);
    setHistoryIndex(newHistory.length - 1);
  };

  // Track canvas changes for history
  useEffect(() => {
    if (!canvas) return;

    const events = ['object:added', 'object:removed', 'object:modified', 'text:changed'];

    // Debounce to avoid too many saves
    let timeout;
    const handleSave = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => saveState(), 100);
    };

    events.forEach(e => canvas.on(e, handleSave));

    // Save initial state if hydration is already complete when the canvas arrives
    // (fast-API path). The slow-API path is handled by the isHydrating effect below.
    setTimeout(() => saveState(), 200);

    return () => {
      clearTimeout(timeout);
      events.forEach(e => canvas.off(e, handleSave));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas]);

  // When hydration finishes, save the fully-loaded canvas as the initial history
  // entry. This replaces the old "empty canvas" entry that the 200ms timer used
  // to capture when the API call took longer than 200 ms.
  useEffect(() => {
    if (isHydrating || !canvas) return;
    setTimeout(() => saveState(), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrating, canvas]);

  // Undo
  const undo = async () => {
    const currentIndex = historyRef.current.index;
    const currentHistory = historyRef.current.history;
    
    if (currentIndex <= 0 || !canvas) return;
    
    isUndoRedo.current = true;
    const newIndex = currentIndex - 1;
    
    try {
      const json = JSON.parse(currentHistory[newIndex]);
      // Fabric v6 serializes canvas background as json.background (not json.backgroundColor).
      // loadFromJSON already restores it; we only apply it explicitly as a safety net for
      // older snapshots that may have used the legacy key.
      const bgColor = json.background || json.backgroundColor || '';

      await canvas.loadFromJSON(json);
      // Restore custom properties Fabric v6 drops during loadFromJSON
      const rawUndoObjs = json.objects || [];
      canvas.getObjects().forEach((obj, i) => {
        if (rawUndoObjs[i]?.name) obj.name = rawUndoObjs[i].name;
        if (!obj._collabId && rawUndoObjs[i]?._collabId) obj._collabId = rawUndoObjs[i]._collabId;
        if (rawUndoObjs[i]?._locked) {
          obj._locked = true;
          obj.set({
            lockMovementX: true,
            lockMovementY: true,
            lockRotation: true,
            lockScalingX: true,
            lockScalingY: true,
            lockSkewingX: true,
            lockSkewingY: true,
            hasControls: false,
          });
          if (obj.type === 'textbox' || obj.type === 'i-text') {
            obj.set({ editable: false });
          }
        }
        if (obj.name === 'border-rect') {
          obj.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });
        }
      });
      // Only set backgroundColor when the snapshot explicitly carried one.
      // Do NOT fall back to '#ffffff' here — loadFromJSON already restored the
      // correct value, and a hard-coded fallback was the root cause of the
      // "canvas goes white on first undo" bug.
      if (bgColor) canvas.backgroundColor = bgColor;
      canvas.requestRenderAll();
      onCanvasChange?.();
      onUndoRedo?.();

      historyRef.current.index = newIndex;
      setHistoryIndex(newIndex);
    } catch (err) {
      console.error('Undo failed:', err);
    } finally {
      // Small delay before allowing new history saves
      setTimeout(() => {
        isUndoRedo.current = false;
      }, 400);
    }
  };

  // Redo
  const redo = async () => {
    const currentIndex = historyRef.current.index;
    const currentHistory = historyRef.current.history;
    
    if (currentIndex >= currentHistory.length - 1 || !canvas) return;
    
    isUndoRedo.current = true;
    const newIndex = currentIndex + 1;
    
    try {
      const json = JSON.parse(currentHistory[newIndex]);
      // Same Fabric v6 key fix as undo — use json.background, not json.backgroundColor.
      const bgColor = json.background || json.backgroundColor || '';

      await canvas.loadFromJSON(json);
      // Restore custom properties Fabric v6 drops during loadFromJSON
      const rawRedoObjs = json.objects || [];
      canvas.getObjects().forEach((obj, i) => {
        if (rawRedoObjs[i]?.name) obj.name = rawRedoObjs[i].name;
        if (!obj._collabId && rawRedoObjs[i]?._collabId) obj._collabId = rawRedoObjs[i]._collabId;
        if (rawRedoObjs[i]?._locked) {
          obj._locked = true;
          obj.set({
            lockMovementX: true,
            lockMovementY: true,
            lockRotation: true,
            lockScalingX: true,
            lockScalingY: true,
            lockSkewingX: true,
            lockSkewingY: true,
            hasControls: false,
          });
          if (obj.type === 'textbox' || obj.type === 'i-text') {
            obj.set({ editable: false });
          }
        }
        if (obj.name === 'border-rect') {
          obj.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });
        }
      });
      if (bgColor) canvas.backgroundColor = bgColor;
      canvas.requestRenderAll();
      onCanvasChange?.();
      onUndoRedo?.();

      historyRef.current.index = newIndex;
      setHistoryIndex(newIndex);
    } catch (err) {
      console.error('Redo failed:', err);
    } finally {
      // Small delay before allowing new history saves
      setTimeout(() => {
        isUndoRedo.current = false;
      }, 400);
    }
  };

  useImperativeHandle(ref, () => ({ undo, redo }), [canvas]);

  // 🗑 Delete selected object(s)
  const deleteSelected = () => {
    const obj = canvas?.getActiveObject();
    if (!obj) return;
    if (obj._locked) return; // locked objects can't be deleted
    if (obj.type === 'activeselection') {
      const items = [...obj.getObjects()].filter(o => !o._locked);
      items.forEach((o) => canvas.remove(o));
      canvas.discardActiveObject();
    } else {
      canvas.remove(obj);
      canvas.discardActiveObject();
    }
    canvas.requestRenderAll();
  };

  // Duplicate selected object — strip annotation IDs so the clone starts clean
  const duplicateSelected = () => {
    const obj = canvas?.getActiveObject();
    if (!obj) return;
    if (obj.name === 'isbn-barcode') return; // only one ISBN allowed
    obj.clone().then((cloned) => {
      // Strip annotation IDs/labels and assign a new unique name so the
      // duplicate is distinct from the original in the annotations panel.
      const stripAndRename = (o) => {
        delete o._collabId;
        delete o._label;
        const m = o.name?.match(/^shape-([a-z]+)\d*$/);
        if (m) {
          const type = m[1];
          if (!canvas._shapeCounters) canvas._shapeCounters = {};
          const nextNum = (canvas._shapeCounters[type] || 0) + 1;
          canvas._shapeCounters[type] = nextNum;
          o.name = `shape-${type}${nextNum}`;
        }
        if (typeof o.getObjects === 'function') o.getObjects().forEach(stripAndRename);
      };
      stripAndRename(cloned);
      cloned.set({ left: obj.left + 20, top: obj.top + 20 });
      canvas.add(cloned);
      canvas.setActiveObject(cloned);
      canvas.requestRenderAll();
      onCanvasChange?.();
    });
  };

  // Layer controls
  const bringToFront = () => {
    const obj = canvas?.getActiveObject();
    if (obj) {
      canvas.bringObjectToFront(obj);
      canvas.requestRenderAll();
    }
  };

  const sendToBack = () => {
    const obj = canvas?.getActiveObject();
    if (obj) {
      canvas.sendObjectToBack(obj);
      canvas.requestRenderAll();
    }
  };

  const groupSelected = () => {
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (!active || active.type !== 'activeselection') return;

    const selectedObjects = active.getObjects();
    if (!selectedObjects || selectedObjects.length < 2) return;

    let groupClip = null;
    selectedObjects.forEach((obj) => {
      if (obj.clipPath && !groupClip) groupClip = obj.clipPath;
      obj.clipPath = undefined;
    });

    // Suppress annotation DB cleanup during grouping — objects are moving
    // inside a group, not being deleted. Their _collabId threads must survive.
    canvas._suppressAnnotationCleanup = true;
    canvas.discardActiveObject();
    selectedObjects.forEach((obj) => canvas.remove(obj));
    canvas._suppressAnnotationCleanup = false;

    const group = new Group(selectedObjects);
    if (groupClip) group.clipPath = groupClip;
    canvas.add(group);
    canvas.setActiveObject(group);
    canvas.requestRenderAll();
    if (onCanvasChange) onCanvasChange();
  };

  // Ungroup a Group back into individual objects
  const ungroupSelected = () => {
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (!active || active.type !== 'group') return;
    const groupClip = active.clipPath || null;
    const items = active.removeAll();
    canvas.remove(active);
    items.forEach(obj => {
      if (groupClip) obj.clipPath = groupClip;
      canvas.add(obj);
    });
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    if (onCanvasChange) onCanvasChange();
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!canvas) return;

    const handleKeyDown = (e) => {
      // Don't trigger if typing in input/textarea or editing text on canvas
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const activeObj = canvas.getActiveObject();
      if (activeObj && activeObj.isEditing) return;
      
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const obj = canvas.getActiveObject();
        if (obj && !obj.isEditing) {
          if (obj._locked) return; // locked objects can't be deleted
          e.preventDefault();
          if (obj.type === 'activeselection') {
            const items = [...obj.getObjects()].filter(o => !o._locked);
            items.forEach((o) => canvas.remove(o));
            canvas.discardActiveObject();
          } else {
            canvas.remove(obj);
            canvas.discardActiveObject();
          }
          canvas.requestRenderAll();
        }
      }
      // Ctrl+D for duplicate
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        duplicateSelected();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas]);

  // Text update — applies to selection only when text is highlighted, else whole object
  const PER_CHAR_PROPS = ['fill', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'underline', 'overline', 'linethrough', 'textBackgroundColor'];
  const updateText = (prop, value) => {
    if (!activeText || !canvas) return;
    if (
      PER_CHAR_PROPS.includes(prop) &&
      activeText.isEditing &&
      activeText.selectionStart !== activeText.selectionEnd
    ) {
      activeText.setSelectionStyles({ [prop]: value });
    } else {
      activeText.set(prop, value);
    }
    canvas.requestRenderAll();
    onCanvasChange?.();
    refreshTextStyles(activeText); // re-read from object so buttons update immediately
  };

  // Shape update
  const updateShape = (prop, value) => {
    const obj = canvas?.getActiveObject();
    if (!obj) return;
    if (['rect', 'circle', 'triangle', 'polygon', 'line'].includes(obj.type)) {
      obj.set(prop, value);
      canvas.requestRenderAll();
    }
  };

  // Add shape
  const addShape = (shapeType) => {
    if (!canvas) return;

    let shape;
    // Object-space center: accounts for pan AND zoom in the viewportTransform
    const vpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
    const z = vpt[0] || 1;
    const cx = canvas.getWidth()  / 2 / z - vpt[4] / z;
    const cy = canvas.getHeight() / 2 / z - vpt[5] / z;

    switch (shapeType) {
      case 'rectangle':
        shape = new Rect({
          left: cx - 50,
          top: cy - 35,
          width: 100,
          height: 70,
          fill: '#cccccc',
          stroke: '#000000',
          strokeWidth: 1,
        });
        break;
      case 'circle':
        shape = new Circle({
          left: cx - 40,
          top: cy - 40,
          radius: 40,
          fill: '#cccccc',
          stroke: '#000000',
          strokeWidth: 1,
        });
        break;
      case 'triangle':
        shape = new Triangle({
          left: cx - 50,
          top: cy - 43,
          width: 100,
          height: 86,
          fill: '#cccccc',
          stroke: '#000000',
          strokeWidth: 1,
        });
        break;
      case 'star':
        // Create a 5-point star
        const outerRadius = 50;
        const innerRadius = 25;
        const points = [];
        for (let i = 0; i < 10; i++) {
          const radius = i % 2 === 0 ? outerRadius : innerRadius;
          const angle = (Math.PI / 5) * i - Math.PI / 2;
          points.push({
            x: cx + radius * Math.cos(angle),
            y: cy + radius * Math.sin(angle)
          });
        }
        shape = new Polygon(points, {
          fill: '#f1c40f',
          stroke: '#000000',
          strokeWidth: 1,
        });
        break;
      case 'line':
        shape = new Line([cx - 60, cy, cx + 60, cy], {
          stroke: '#000000',
          strokeWidth: 3,
        });
        break;
      case 'arrow':
        // Simple arrow using polygon
        shape = new Polygon([
          { x: cx - 60, y: cy - 10 },
          { x: cx + 20, y: cy - 10 },
          { x: cx + 20, y: cy - 25 },
          { x: cx + 60, y: cy },
          { x: cx + 20, y: cy + 25 },
          { x: cx + 20, y: cy + 10 },
          { x: cx - 60, y: cy + 10 },
        ], {
          fill: '#3498db',
          stroke: '#000000',
          strokeWidth: 1,
        });
        break;
      default:
        return;
    }

    // Tag shapes with a unique numbered name so each instance is identifiable
    // in the annotation panel and the loader won't mistake it for a freehand stroke.
    // _shapeCounters is initialised from loaded objects in useCanvasSetup (Pass 5)
    // and incremented here — guaranteed unique across refreshes and deletions.
    if (!canvas._shapeCounters) canvas._shapeCounters = {};
    const nextNum = (canvas._shapeCounters[shapeType] || 0) + 1;
    canvas._shapeCounters[shapeType] = nextNum;
    shape.set({ name: `shape-${shapeType}${nextNum}` });

    canvas.add(shape);
    canvas.setActiveObject(shape);
    canvas.requestRenderAll();
    setShowShapesMenu(false);
  };

  // Add border
  const addBorder = () => {
    if (!canvas) return;
    const percentage = Math.max(1, Math.min(99, borderSizePct));

    // Object-space center and sizes: accounts for pan AND zoom
    const vpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
    const z   = vpt[0] || 1;
    const borderWidth  = (canvas.getWidth()  / z * percentage) / 100;
    const borderHeight = (canvas.getHeight() / z * percentage) / 100;
    const vcx = canvas.getWidth()  / 2 / z - vpt[4] / z;
    const vcy = canvas.getHeight() / 2 / z - vpt[5] / z;

    const borderRect = new Rect({
      name: 'border-rect',
      left: vcx - borderWidth / 2,
      top: vcy - borderHeight / 2,
      width: borderWidth,
      height: borderHeight,
      fill: 'transparent',
      stroke: '#ffffff',
      strokeWidth: 4,
      strokeUniform: true,
      lockUniScaling: true,
      lockMovementX: true,
      lockMovementY: true,
      centeredScaling: true,
    });

    borderRect.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });

    canvas.add(borderRect);
    canvas.setActiveObject(borderRect);
    canvas.requestRenderAll();
  };

  // Border/stroke color update
  const updateStrokeColor = (color) => {
    const obj = canvas?.getActiveObject();
    if (obj) {
      obj.set('stroke', color);
      canvas.requestRenderAll();
      onCanvasChange?.();
    }
  };

  // Border/stroke style update (solid/dashed/dash-dot/dotted/none)
  const updateBorderStyle = (style) => {
    const obj = canvas?.getActiveObject();
    if (!obj) return;
    const width = obj.strokeWidth || 1;
    obj.set({
      _borderStyle: style,
      stroke: style === 'none' ? null : (obj.stroke || '#000000'),
      strokeDashArray: getBorderDashArray(style, width),
      strokeLineCap: getBorderLineCap(style),
    });
    canvas.requestRenderAll();
    onCanvasChange?.();
    setBorderState(prev => ({ ...prev, style }));
  };

  // Canvas background update
  const updateCanvasBackground = (color) => {
    if (onSetFaceBackground) {
      onSetFaceBackground(color);
      return;
    }
    if (canvas) {
      // Direct property assignment works for Fabric v6
      canvas.backgroundColor = color;
      canvas.requestRenderAll();
      onCanvasChange?.(); // background changes don't fire object:* events
    }
  };

  // Load an <img> element from a src, optionally CORS-enabled (needed so the
  // canvas stays exportable when the image comes from S3). Resolves once loaded.
  const loadImageEl = (src, crossOrigin) => new Promise((resolve, reject) => {
    const imgElement = new Image();
    if (crossOrigin) imgElement.crossOrigin = crossOrigin; // must be set before src
    imgElement.onload = () => resolve(imgElement);
    imgElement.onerror = reject;
    imgElement.src = src;
  });

  // Add a loaded <img> to the canvas, centered and scaled to fit the face.
  const placeImageOnCanvas = (imgElement, opts = {}) => {
    // Object-space center: accounts for pan AND zoom in the viewportTransform
    const vpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
    const z = vpt[0] || 1;
    const cx = canvas.getWidth()  / 2 / z - vpt[4] / z;
    const cy = canvas.getHeight() / 2 / z - vpt[5] / z;

    // Scale to fit the face (in object space)
    const maxWidth  = canvas.getWidth()  / z * 0.8;
    const maxHeight = canvas.getHeight() / z * 0.8;
    let scale = 1;
    if (imgElement.width > maxWidth || imgElement.height > maxHeight) {
      scale = Math.min(maxWidth / imgElement.width, maxHeight / imgElement.height);
    }
    const fabricImage = new FabricImage(imgElement, {
      left: cx - (imgElement.width * scale) / 2,
      top: cy - (imgElement.height * scale) / 2,
      scaleX: scale,
      scaleY: scale,
      ...opts,
    });
    canvas.add(fabricImage);
    canvas.setActiveObject(fabricImage);
    canvas.requestRenderAll();
    // canvas.add() fires object:added, which already drives autosave/history.
  };

  // Image upload handler — upload the file to S3 and reference it by URL so large
  // images stay OUT of the design JSON (stored inline in the draft doc, 16MB cap).
  //
  // A failed upload used to fall back to embedding the base64 data URL here.
  // That was silent, and it was the root of the "cover won't save" bug: the
  // embedded blob grew the design past the 16MB cap, after which every save of
  // that cover returned 413 — unrecoverably, because the 413 told the user to
  // re-add the image and re-adding hit this same fallback. A failed upload now
  // surfaces and places nothing, so the design can never be poisoned.
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !canvas) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target.result;

      if (!onUploadImageAsset) {
        console.warn('[TextStyleToolbar] no image uploader wired — refusing to embed');
        toast.error('Images cannot be added here.');
        return;
      }

      let url = null;
      try {
        url = await onUploadImageAsset(dataUrl);
      } catch (err) {
        console.warn('[TextStyleToolbar] image upload threw:', err);
      }

      if (!url) {
        toast.error(
          "Couldn't upload that image, so it wasn't added. " +
          'Check your connection and try again.',
        );
        return;
      }

      try {
        // crossOrigin so canvas.toDataURL() export doesn't taint the canvas.
        const imgElement = await loadImageEl(url, 'anonymous');
        placeImageOnCanvas(imgElement, { crossOrigin: 'anonymous' });
      } catch (err) {
        console.warn('[TextStyleToolbar] uploaded image failed to load:', err);
        toast.error("That image uploaded but couldn't be displayed. Try again.");
      }
    };
    reader.readAsDataURL(file);
    // Reset input
    e.target.value = '';
  };

  // Update opacity
  const updateOpacity = (value) => {
    const obj = canvas?.getActiveObject();
    if (obj) {
      obj.set('opacity', value / 100);
      canvas.requestRenderAll();
      onCanvasChange?.();
    }
    setLocalOpacity(value);
  };

  // ── Flip helpers ──
  const flipHorizontal = () => {
    const obj = canvas?.getActiveObject();
    if (obj) { obj.set('flipX', !obj.flipX); canvas.requestRenderAll(); onCanvasChange?.(); }
  };
  const flipVertical = () => {
    const obj = canvas?.getActiveObject();
    if (obj) { obj.set('flipY', !obj.flipY); canvas.requestRenderAll(); onCanvasChange?.(); }
  };

  // ── Rotate helper ──
  const rotateObject = (deg) => {
    const obj = canvas?.getActiveObject();
    if (obj) { obj.rotate((obj.angle || 0) + deg); canvas.requestRenderAll(); onCanvasChange?.(); }
  };

  // ── Crop (basic: reset clip to current selection box) ──
  const cropImage = () => {
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== 'image') { toast.error('Select an image to crop.'); return; }
    // Use the current object dimensions as the visible area
    const cropX = obj.cropX || 0;
    const cropY = obj.cropY || 0;
    const w = obj.width;
    const h = obj.height;
    const newCropX = cropX + w * 0.1;
    const newCropY = cropY + h * 0.1;
    const newW = w * 0.8;
    const newH = h * 0.8;
    obj.set({ cropX: newCropX, cropY: newCropY, width: newW, height: newH });
    obj.setCoords();
    canvas.requestRenderAll();
    onCanvasChange?.();
  };

  const resetCrop = () => {
    const obj = canvas?.getActiveObject();
    if (!obj || obj.type !== 'image') return;
    obj.set({ cropX: 0, cropY: 0, width: obj.getElement().naturalWidth, height: obj.getElement().naturalHeight });
    obj.setCoords();
    canvas.requestRenderAll();
    onCanvasChange?.();
  };

  // ── Toggle Lock ──
  const toggleLock = () => {
    const obj = canvas?.getActiveObject();
    if (!obj) return;
    const newLockedState = !isObjectLocked;
    obj._locked = newLockedState;
    // Keep object selectable so it can be unlocked, but prevent modifications
    obj.set({
      lockMovementX: newLockedState,
      lockMovementY: newLockedState,
      lockRotation: newLockedState,
      lockScalingX: newLockedState,
      lockScalingY: newLockedState,
      lockSkewingX: newLockedState,
      lockSkewingY: newLockedState,
      hasControls: !newLockedState,
    });
    // Prevent text editing on locked textboxes
    if (obj.type === 'textbox' || obj.type === 'i-text') {
      obj.set({ editable: !newLockedState });
      if (newLockedState && obj.isEditing) obj.exitEditing();
    }
    setIsObjectLocked(newLockedState);
    canvas.fire('object:modified', { target: obj }); // triggers history save
    canvas.requestRenderAll();
    onCanvasChange?.();
  };

  // ── Shadow ──
  const toggleShadow = () => {
    const obj = canvas?.getActiveObject();
    if (!obj) return;
    if (obj.shadow) {
      obj.shadow = null;
      setShadowState(prev => ({ ...prev, on: false }));
    } else {
      const s = { color: shadowState.color || '#000000', blur: shadowState.blur, offsetX: shadowState.offsetX, offsetY: shadowState.offsetY };
      obj.shadow = new Shadow(s);
      setShadowState(prev => ({ ...prev, on: true }));
    }
    canvas.requestRenderAll();
    onCanvasChange?.();
  };
  const updateShadow = (prop, value) => {
    const obj = canvas?.getActiveObject();
    if (!obj || !obj.shadow) return;
    const updated = { color: obj.shadow.color, blur: obj.shadow.blur, offsetX: obj.shadow.offsetX, offsetY: obj.shadow.offsetY, [prop]: value };
    obj.shadow = new Shadow(updated);
    setShadowState(prev => ({ ...prev, [prop]: value }));
    canvas.requestRenderAll();
    onCanvasChange?.();
  };

  // ── Shade (semi-transparent color overlay via a gradient) ──
  const applyShade = (color) => {
    const obj = canvas?.getActiveObject();
    if (!obj) return;
    // Store the original fill if this is the first shade
    if (!obj._origFill && obj._origFill !== '') obj._origFill = obj.fill;
    obj.set('fill', color);
    canvas.requestRenderAll();
    onCanvasChange?.();
  };
  const removeShade = () => {
    const obj = canvas?.getActiveObject();
    if (!obj) return;
    if (obj._origFill !== undefined) { obj.set('fill', obj._origFill); delete obj._origFill; }
    else obj.set('fill', 'transparent');
    canvas.requestRenderAll();
    onCanvasChange?.();
  };

  // ── Text Shadow ──
  const toggleTextShadow = () => {
    if (!activeText || !canvas) return;
    if (activeText.shadow && activeText._effectType !== 'glow') {
      activeText.shadow = null;
      activeText._effectType = null;
      setTextShadowState(prev => ({ ...prev, on: false }));
    } else {
      activeText.shadow = new Shadow({
        color: textShadowState.color || '#000000',
        blur: textShadowState.blur,
        offsetX: textShadowState.offsetX,
        offsetY: textShadowState.offsetY,
      });
      activeText._effectType = 'shadow';
      setTextShadowState(prev => ({ ...prev, on: true }));
      setGlowState(prev => ({ ...prev, on: false }));
    }
    canvas.requestRenderAll();
    onCanvasChange?.();
  };
  const updateTextShadow = (prop, value) => {
    if (!activeText || !activeText.shadow) return;
    const s = activeText.shadow;
    activeText.shadow = new Shadow({
      color: s.color, blur: s.blur, offsetX: s.offsetX, offsetY: s.offsetY,
      [prop]: value,
    });
    setTextShadowState(prev => ({ ...prev, [prop]: value }));
    canvas.requestRenderAll();
    onCanvasChange?.();
  };

  // ── Text Glow ── (uses shadow slot with offsetX=0, offsetY=0)
  const toggleGlow = () => {
    if (!activeText || !canvas) return;
    if (activeText.shadow && activeText._effectType === 'glow') {
      activeText.shadow = null;
      activeText._effectType = null;
      setGlowState(prev => ({ ...prev, on: false }));
    } else {
      const glowColor = glowState.color || '#ffff00';
      activeText.shadow = new Shadow({
        color: glowColor,
        blur: glowState.thickness,
        offsetX: 0,
        offsetY: 0,
      });
      activeText._glowOriginalColor = glowColor;
      activeText._glowBrightness = 0;
      activeText._effectType = 'glow';
      setGlowState(prev => ({ ...prev, on: true, brightness: 0 }));
      setTextShadowState(prev => ({ ...prev, on: false }));
    }
    canvas.requestRenderAll();
    onCanvasChange?.();
  };
  const hexToHsl = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    const rn = r / 255, gn = g / 255, bn = b / 255;
    const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case rn: h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6; break;
        case gn: h = ((bn - rn) / d + 2) / 6; break;
        case bn: h = ((rn - gn) / d + 4) / 6; break;
      }
    }
    return { h, s, l };
  };

  const hslToHex = (h, s, l) => {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const r = Math.round(hue2rgb(p, q, h + 1/3) * 255);
    const g = Math.round(hue2rgb(p, q, h) * 255);
    const b = Math.round(hue2rgb(p, q, h - 1/3) * 255);
    return rgbToHex(r, g, b);
  };

  const adjustBrightness = (hex, amount) => {
    const { h, s, l } = hexToHsl(hex);
    const adjustment = amount / 100 * 0.5; // brightness adjustment range: -50% to +50% of lightness
    const newL = Math.max(0, Math.min(1, l + adjustment));
    return hslToHex(h, s, newL);
  };

  const updateGlow = (prop, value) => {
    if (!activeText || !activeText.shadow || activeText._effectType !== 'glow') return;
    let glowColor = activeText.shadow.color;
    if (prop === 'color') {
      glowColor = value;
      activeText._glowOriginalColor = value;
      activeText._glowBrightness = 0;
    } else if (prop === 'brightness') {
      activeText._glowBrightness = value;
      const baseColor = activeText._glowOriginalColor || activeText.shadow.color;
      glowColor = adjustBrightness(baseColor, value);
    }
    activeText.shadow = new Shadow({
      color: glowColor,
      blur: prop === 'thickness' ? value : activeText.shadow.blur,
      offsetX: 0,
      offsetY: 0,
    });
    setGlowState(prev => ({ ...prev, [prop]: value }));
    canvas.requestRenderAll();
    onCanvasChange?.();
  };

  // ── Text Reflection ── (mirrored copy drawn below via prototype _render patch)
  // Reflection now uses text's actual angle instead of independent rotation
  const toggleReflection = () => {
    if (!activeText || !canvas) return;
    const cur = activeText._reflection;
    if (cur && cur.on) {
      activeText._reflection = { ...cur, on: false };
      setReflectionState(prev => ({ ...prev, on: false }));
    } else {
      const reflColor = reflectionState.color || '';
      const next = {
        on: true,
        color: reflColor,
        brightness: reflectionState.brightness || 0,
        amount: reflectionState.amount || 50,
        fade: reflectionState.fade ?? false,
        size: reflectionState.size ?? 100,
        gap: reflectionState.gap ?? 0,
        blur: reflectionState.blur ?? 0,
        direction: reflectionState.direction ?? 'below',
        blendMode: reflectionState.blendMode ?? 'source-over',
        rainbow: reflectionState.rainbow ?? false,
        gradientColor1: reflectionState.gradientColor1 ?? '',
        gradientColor2: reflectionState.gradientColor2 ?? '',
        glitch: reflectionState.glitch ?? 0,
        wave: reflectionState.wave ?? 0,
        waveFreq: reflectionState.waveFreq ?? 3,
      };
      activeText._reflection = next;
      activeText._reflectionOriginalColor = reflColor;
      activeText._reflectionBrightness = 0;
      setReflectionState(next);
    }
    activeText.dirty = true;
    canvas.requestRenderAll();
    onCanvasChange?.();
  };
  const updateReflection = (prop, value) => {
    if (!activeText || !activeText._reflection) return;
    if (prop === 'color') {
      activeText._reflection = { ...activeText._reflection, color: value };
      activeText._reflectionOriginalColor = value;
      activeText._reflectionBrightness = 0;
    } else if (prop === 'brightness') {
      activeText._reflection = { ...activeText._reflection, brightness: value };
      activeText._reflectionBrightness = value;
    } else {
      activeText._reflection = { ...activeText._reflection, [prop]: value };
    }
    setReflectionState(prev => ({ ...prev, [prop]: value }));
    activeText.dirty = true;
    canvas.requestRenderAll();
    onCanvasChange?.();
  };

  // ── Text Shade ── (-100 dark … 0 none … +100 light)
  const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') return { r: 0, g: 0, b: 0 };
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const n = parseInt(h, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  };
  const rgbToHex = (r, g, b) => {
    const to = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    return `#${to(r)}${to(g)}${to(b)}`;
  };
  const mixShade = (hex, amount) => {
    const { r, g, b } = hexToRgb(hex);
    const t = Math.abs(amount) / 100;
    if (amount >= 0) return rgbToHex(r + (255 - r) * t, g + (255 - g) * t, b + (255 - b) * t);
    return rgbToHex(r * (1 - t), g * (1 - t), b * (1 - t));
  };
  const updateTextShade = (amount) => {
    if (!activeText || !canvas) return;
    if (activeText._origShadeFill === undefined) {
      activeText._origShadeFill = typeof activeText.fill === 'string' ? activeText.fill : '#000000';
    }
    const base = activeText._origShadeFill || '#000000';
    if (amount === 0) {
      activeText.set('fill', base);
      delete activeText._origShadeFill;
      activeText._shadeAmount = 0;
    } else {
      activeText.set('fill', mixShade(base, amount));
      activeText._shadeAmount = amount;
    }
    setTextShadeAmount(amount);
    canvas.requestRenderAll();
    onCanvasChange?.();
  };

  // ── Stroke width for shapes ──
  const updateStrokeWidth = (w) => {
    const obj = canvas?.getActiveObject();
    if (obj) {
      const style = obj._borderStyle || 'solid';
      obj.set({
        strokeWidth: w,
        stroke: obj.stroke || '#000000',
        strokeDashArray: getBorderDashArray(style, w),
      });
      canvas.requestRenderAll();
      onCanvasChange?.();
      setBorderState(prev => ({ ...prev, width: w }));
    }
  };

  // ═══════════════════════════════════════════════════════════
  // ── DRAW tab helpers ──
  // ═══════════════════════════════════════════════════════════

  // Generate an SVG circle cursor for the eraser, sized to match the brush width
  const makeEraserCursor = (size) => {
    const s = Math.max(8, Math.min(128, size));
    const half = s / 2;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}"><circle cx="${half}" cy="${half}" r="${half - 1}" fill="none" stroke="red" stroke-width="1.5" opacity="0.7"/><circle cx="${half}" cy="${half}" r="1.5" fill="red" opacity="0.6"/></svg>`;
    const encoded = encodeURIComponent(svg);
    return `url("data:image/svg+xml,${encoded}") ${half} ${half}, crosshair`;
  };

  // Determine which face region a canvas x-coordinate belongs to
  const getFaceAtX = (x) => {
    const fw = faceDims?.faceW || 360;
    const sw = faceDims?.spineW || 100;
    if (x < fw) return 'back';
    if (x < fw + sw) return 'spine';
    return 'front';
  };

  // Map the active view to a face name (null for full view)
  const getActiveFace = () => {
    if (activeView === 'front') return 'front';
    if (activeView === 'back') return 'back';
    if (activeView === 'spine' || activeView === 'onshelf') return 'spine';
    return null; // full / 3d
  };

  // Protected system objects the eraser must never touch
  const ERASER_PROTECTED = ['margin-guide', 'isbn-barcode', 'bg-front', 'bg-spine', 'bg-back'];

  // Check whether an object is a drawing stroke eligible for erasing.
  // Explicitly tagged strokes always match. Untagged path/group objects are
  // included for backward compatibility (strokes drawn before tagging existed)
  // while named system objects are always protected.
  const isDrawStroke = (obj) => {
    if (obj.name === 'draw-stroke' || obj.name === 'eraser-path') return true;
    if (obj.name && ERASER_PROTECTED.includes(obj.name)) return false;
    // Protect text, images, and explicitly-named design elements
    if (obj.type === 'textbox' || obj.type === 'i-text' || obj.type === 'text') return false;
    if (obj.type === 'image') return false;
    if (obj.type === 'rect' || obj.type === 'triangle' || obj.type === 'polygon') return false;
    // Untagged paths = freehand pencil/marker strokes (backward compat)
    if (obj.type === 'path') return true;
    // Untagged groups with no name = CircleBrush paint blobs (backward compat)
    if (obj.type === 'group' && !obj.name) return true;
    // Circles without a name (from old CircleBrush single dots)
    if (obj.type === 'circle' && !obj.name) return true;
    return false;
  };

  // Check whether an object's centre falls within the currently visible face region
  const isInCurrentFace = (obj) => {
    const face = getActiveFace();
    if (!face) return true; // full view — everything is visible
    const fw = faceDims?.faceW || 360;
    const sw = faceDims?.spineW || 100;
    const fh = faceDims?.faceH || 540;
    const regionMap = {
      front: { left: fw + sw, right: fw * 2 + sw, top: 0, bottom: fh },
      spine: { left: fw, right: fw + sw, top: 0, bottom: fh },
      back:  { left: 0, right: fw, top: 0, bottom: fh },
    };
    const r = regionMap[face];
    const cx = (obj.left || 0) + (obj.width || 0) * (obj.scaleX || 1) / 2;
    const cy = (obj.top || 0) + (obj.height || 0) * (obj.scaleY || 1) / 2;
    return cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom;
  };

  const applyBrush = (tool, color, width, opacity, overrideStyle) => {
    if (!canvas) return;
    const style = overrideStyle ?? strokeStyle;

    if (tool === 'erase') {
      const cursor = makeEraserCursor(Math.max(width, 8));
      canvas.selection = false;
      canvas.forEachObject(o => { o.selectable = false; o.evented = false; });

      if (eraseMode === 'area') {
        const bgColor = faceBackgroundColor || '#ffffff';
        canvas.isDrawingMode = true;
        const brush = new PencilBrush(canvas);
        brush.color = bgColor;
        brush.width = Math.max(width, 8);
        brush.strokeLineCap = 'round';
        canvas.freeDrawingBrush = brush;
        canvas.freeDrawingCursor = cursor;
      } else {
        canvas.isDrawingMode = false;
        canvas.defaultCursor = cursor;
        canvas.hoverCursor = cursor;
      }
      return;
    }

    canvas.selection = false;
    canvas.forEachObject(o => { o.selectable = false; o.evented = false; });
    canvas.isDrawingMode = true;
    canvas.freeDrawingCursor = 'crosshair';
    canvas.defaultCursor = 'default';
    canvas.hoverCursor = 'move';

    const rgba = hexToRgba(color, opacity / 100);
    let brush;

    // Tool-specific overrides — paint and marker have fixed behavior regardless of strokeStyle
    if (tool === 'marker') {
      brush = new PencilBrush(canvas);
      brush.color = hexToRgba(color, Math.min(opacity, 100) / 100);
      brush.width = Math.max(width * 2.5, 8);
      brush.strokeLineCap = 'square';
      canvas.freeDrawingBrush = brush;
      return;
    }
    if (tool === 'paint') {
      brush = new CircleBrush(canvas);
      brush.color = hexToRgba(color, Math.min(opacity, 80) / 100);
      brush.width = Math.max(width, 4);
      canvas.freeDrawingBrush = brush;
      return;
    }

    switch (style) {
      case 'gouache': {
        brush = new PencilBrush(canvas);
        brush.color = hexToRgba(color, Math.min(opacity, 92) / 100);
        brush.width = Math.max(width * 1.5, 4);
        brush.strokeLineCap = 'round';
        brush.shadow = new Shadow({ blur: 1, offsetX: 0, offsetY: 0, color: hexToRgba(color, 0.3) });
        break;
      }
      case 'oil-paint': {
        brush = new PencilBrush(canvas);
        brush.color = hexToRgba(color, Math.min(opacity, 85) / 100);
        brush.width = Math.max(width, 3);
        brush.strokeLineCap = 'round';
        brush.shadow = new Shadow({ blur: 3, offsetX: 0, offsetY: 0, color: hexToRgba(color, 0.5) });
        break;
      }
      case 'oil-paint-flat': {
        brush = new PencilBrush(canvas);
        brush.color = hexToRgba(color, opacity / 100);
        brush.width = Math.max(width * 2.5, 8);
        brush.strokeLineCap = 'square';
        break;
      }
      case 'color-change': {
        const hsl = hexToHsl(color);
        const shiftedHex = hslToHex((hsl.h + 60) % 360, hsl.s, hsl.l);
        brush = new PencilBrush(canvas);
        brush.color = rgba;
        brush.width = width;
        brush.strokeLineCap = 'round';
        brush.shadow = new Shadow({ blur: 6, offsetX: 1, offsetY: 1, color: hexToRgba(shiftedHex, Math.min(opacity, 70) / 100) });
        break;
      }
      case 'bit-husky': {
        brush = new SprayBrush(canvas);
        brush.color = hexToRgba(color, opacity / 100);
        brush.width = Math.min(Math.max(width * 2, 8), 80);
        brush.density = Math.max(4, Math.min(10, Math.round(width * 0.4)));
        brush.dotWidth = Math.max(1, width * 0.3);
        brush.dotWidthVariance = 1;
        brush.randomOpacity = true;
        break;
      }
      case 'rough': {
        brush = new RoughBrush(canvas);
        brush.color = rgba;
        brush.width = width;
        brush.strokeLineCap = 'round';
        break;
      }
      case 'lighter-ink': {
        brush = new PencilBrush(canvas);
        brush.color = hexToRgba(color, Math.max(opacity * 0.35, 5) / 100);
        brush.width = Math.max(Math.round(width * 0.7), 1);
        brush.strokeLineCap = 'round';
        break;
      }
      case 'darker-bleed': {
        brush = new PencilBrush(canvas);
        brush.color = hexToRgba(color, Math.min(opacity, 95) / 100);
        brush.width = Math.max(width * 1.2, 3);
        brush.strokeLineCap = 'round';
        brush.shadow = new Shadow({ blur: 10, offsetX: 0, offsetY: 0, color: hexToRgba(color, 0.7) });
        break;
      }
      case 'light-running-ink': {
        brush = new PencilBrush(canvas);
        brush.color = hexToRgba(color, Math.max(opacity * 0.2, 3) / 100);
        brush.width = Math.max(Math.round(width * 0.5), 1);
        brush.strokeLineCap = 'round';
        break;
      }
      case 'smooth':
      default: {
        brush = new PencilBrush(canvas);
        brush.color = rgba;
        brush.width = width;
        brush.strokeLineCap = 'round';
        break;
      }
    }

    canvas.freeDrawingBrush = brush;
  };

  // Utility: hex + alpha → rgba string
  const hexToRgba = (hex, alpha) => {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16) || 0;
    const g = parseInt(h.substring(2, 4), 16) || 0;
    const b = parseInt(h.substring(4, 6), 16) || 0;
    return `rgba(${r},${g},${b},${alpha})`;
  };

  const activateDrawTool = (tool) => {
    if (drawTool === tool) {
      // Toggle off — deactivate drawing but keep objects locked in draw panel
      setDrawTool(null);
      if (canvas) {
        canvas.isDrawingMode = false;
        canvas.selection = false;
        canvas.defaultCursor = 'default';
        canvas.hoverCursor = 'default';
        canvas.forEachObject(o => { o.selectable = false; o.evented = false; });
      }
    } else {
      setDrawTool(tool);
      applyBrush(tool, drawColor, drawWidth, drawOpacity);
    }
  };

  const updateDrawColor = (color) => {
    setDrawColor(color);
    if (drawTool) applyBrush(drawTool, color, drawWidth, drawOpacity);
  };

  const updateDrawWidth = (w) => {
    setDrawWidth(w);
    if (drawTool) applyBrush(drawTool, drawColor, w, drawOpacity);
  };

  const updateDrawOpacity = (val) => {
    setDrawOpacity(val);
    if (drawTool) applyBrush(drawTool, drawColor, drawWidth, val);
  };

  const updateStrokeStyle = (style) => {
    setStrokeStyle(style);
    if (drawTool && drawTool !== 'erase') applyBrush(drawTool, drawColor, drawWidth, drawOpacity, style);
  };

  // ── Stroke Eraser: drag to remove whole stroke objects ──
  // Uses getPointer (scene coords) + manual object bounds for reliable hit testing.
  // Batches removals into one undo step per drag gesture.
  useEffect(() => {
    if (!canvas) return;
    if (drawTool !== 'erase' || eraseMode !== 'stroke' || activePanel !== 'draw') return;

    const eraserRadius = Math.max(drawWidth / 2, 12);

    // Hit test in scene (object) coordinate space
    const hitTest = (obj, px, py) => {
      const sw = (obj.strokeWidth || 1) / 2;
      const left = (obj.left || 0) - sw;
      const top  = (obj.top  || 0) - sw;
      const w = (obj.width  || 0) * (obj.scaleX || 1) + sw * 2;
      const h = (obj.height || 0) * (obj.scaleY || 1) + sw * 2;
      // Closest point on bounding rect to the cursor
      const cx = Math.max(left, Math.min(px, left + w));
      const cy = Math.max(top,  Math.min(py, top  + h));
      const dx = px - cx;
      const dy = py - cy;
      return (dx * dx + dy * dy) <= (eraserRadius * eraserRadius);
    };

    let isErasing = false;
    let erasedAny = false;
    let rafId = null;
    let pendingEvent = null;

    const processErase = () => {
      rafId = null;
      if (!pendingEvent) return;
      const e = pendingEvent;
      pendingEvent = null;
      // getPointer returns scene coordinates (same space as obj.left / obj.top)
      const pointer = canvas.getPointer(e);
      const objects = canvas.getObjects().slice().reverse();
      let removed = false;
      for (const obj of objects) {
        if (!isDrawStroke(obj)) continue;
        if (hitTest(obj, pointer.x, pointer.y)) {
          canvas.remove(obj);
          removed = true;
          erasedAny = true;
        }
      }
      if (removed) canvas.requestRenderAll();
    };

    const handleMouseDown = (opt) => {
      isErasing = true;
      erasedAny = false;
      isUndoRedo.current = true;
      pendingEvent = opt.e;
      processErase();
    };
    const handleMouseMove = (opt) => {
      if (!isErasing) return;
      pendingEvent = opt.e;
      if (!rafId) rafId = requestAnimationFrame(processErase);
    };
    const handleMouseUp = () => {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      if (pendingEvent) processErase();
      isErasing = false;
      isUndoRedo.current = false;
      if (erasedAny) { saveState(); onCanvasChange?.(); }
      erasedAny = false;
    };

    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [canvas, drawTool, eraseMode, drawWidth, activePanel, onCanvasChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-apply brush when eraseMode, drawWidth, or face background change while erase is active.
  // faceBackgroundColor keeps the area-eraser brush in sync when switching faces.
  useEffect(() => {
    if (!canvas || drawTool !== 'erase' || activePanel !== 'draw') return;
    applyBrush('erase', drawColor, drawWidth, drawOpacity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eraseMode, drawWidth, faceBackgroundColor]);

  // Names of objects that must NEVER become selectable / evented
  const LOCKED_NAMES = ['bg-front', 'bg-spine', 'bg-back', 'margin-guide', 'isbn-barcode', 'eraser-path', 'draw-stroke'];
  const isFreehandObj = (o) => {
    if (o.name === 'draw-stroke' || o.name === 'eraser-path') return true;
    if (o.name && LOCKED_NAMES.includes(o.name)) return false;
    // Untagged freehand paths (pencil/marker drawn before tagging existed)
    if (o.type === 'path' && !o.name) return true;
    // Untagged groups = CircleBrush paint blobs
    if (o.type === 'group' && !o.name) return true;
    // Untagged circles = single-dot paint blobs
    if (o.type === 'circle' && !o.name) return true;
    return false;
  };
  const isLockedObj = (o) => {
    if (o.name && (LOCKED_NAMES.includes(o.name) || o.name.startsWith('bg-'))) return true;
    return isFreehandObj(o);
  };

  // Deactivate drawing when the user switches face views while in the draw panel.
  // Uses a ref to skip the initial mount so we don't interfere with re-renders.
  const prevActiveViewRef = useRef(activeView);
  useEffect(() => {
    if (prevActiveViewRef.current === activeView) return;
    prevActiveViewRef.current = activeView;
    if (activePanel !== 'draw' || !canvas) return;
    setDrawTool(null);
    canvas.isDrawingMode = false;
    canvas.selection = false;
    canvas.defaultCursor = 'default';
    canvas.hoverCursor = 'default';
    canvas.forEachObject(o => { o.selectable = false; o.evented = false; });
  }, [activeView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Turn off drawing mode when leaving draw panel; do NOT auto-activate on entry
  useEffect(() => {
    if (!canvas) return;
    if (activePanel !== 'draw') {
      setDrawTool(null);
      canvas.isDrawingMode = false;
      canvas.selection = true;
      canvas.defaultCursor = 'default';
      canvas.hoverCursor = 'move';
      canvas.forEachObject(o => {
        if (isLockedObj(o)) return;
        o.selectable = true;
        o.evented = true;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePanel, canvas]);

  // After a freehand stroke: tag drawing strokes or apply eraser compositing.
  // path:created covers PencilBrush (pencil / marker / area-eraser).
  // object:added covers CircleBrush (paint) which creates Group objects.
  useEffect(() => {
    if (!canvas) return;
    const onPathCreated = ({ path }) => {
      if (!path) { onCanvasChange?.(); return; }
      if (drawTool === 'erase' && eraseMode === 'area') {
        // Area eraser: tag as eraser-path (painted in bg color, normal compositing).
        // Non-selectable so it isn't accidentally grabbed later.
        path.set({
          name: 'eraser-path',
          selectable: false,
          evented: false,
        });
      } else if (drawTool && drawTool !== 'erase') {
        path.set({ name: 'draw-stroke', selectable: false, evented: false });
      }
      onCanvasChange?.();
    };
    const onObjectAdded = ({ target }) => {
      if (!target || !canvas.isDrawingMode) return;
      if (drawTool && drawTool !== 'erase' && !target.name) {
        target.set({ name: 'draw-stroke', selectable: false, evented: false });
      }
    };
    canvas.on('path:created', onPathCreated);
    canvas.on('object:added', onObjectAdded);
    return () => {
      canvas.off('path:created', onPathCreated);
      canvas.off('object:added', onObjectAdded);
    };
  }, [canvas, drawTool, eraseMode, onCanvasChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get current values for display
  const currentShapeFill = (() => {
    const obj = canvas?.getActiveObject();
    return (obj && ['rect', 'circle', 'triangle', 'polygon'].includes(obj.type) && obj.fill !== 'transparent')
      ? (obj.fill || '#cccccc')
      : '#cccccc';
  })();

  const currentStroke = activeObject?.stroke || '#000000';
  const currentOpacity = localOpacity;

  return (
    <div className="text-style-toolbar-fixed styled">
      {/* ── COVER panel: background, margins, dimensions, thickness, ISBN, blurb ── */}
      {activePanel === 'cover' && (
        <div className="toolbar-row" style={{ flexWrap: 'wrap' }}>
          {/* Background color */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Background</span>
            <div className="dropdown-wrapper">
              <button
                onClick={(e) => { e.stopPropagation(); setShowCanvasBgPalette(!showCanvasBgPalette); }}
                title="Canvas Background"
              >
                Canvas
                <span className="color-box" style={{ backgroundColor: faceBackgroundColor ?? canvas?.backgroundColor ?? '#ffffff' }} />
              </button>
              {showCanvasBgPalette && (
                <div className="dropdown-menu color-grid">
                  {PALETTE.map((color) => (
                    <div
                      key={color}
                      className="color-swatch"
                      style={{ backgroundColor: color }}
                      onClick={() => { updateCanvasBackground(color); setShowCanvasBgPalette(false); }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="toolbar-divider" />

          {/* Margins */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Margins (px)</span>
            {['top', 'right', 'bottom', 'left'].map((side) => (
              <label key={side} style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 11 }}>
                <span style={{ width: 14, fontWeight: 500, color: '#666' }}>
                  {side.charAt(0).toUpperCase()}
                </span>
                <input
                  type="text" inputMode="numeric"
                  value={marginDrafts[side]}
                  onChange={(e) => setMarginDrafts(prev => ({ ...prev, [side]: e.target.value }))}
                  onBlur={() => {
                    const val = Math.max(0, Math.min(100, parseInt(marginDrafts[side]) || 0));
                    setMarginDrafts(prev => ({ ...prev, [side]: String(val) }));
                    onSetMargins?.({ ...coverMargins, [side]: val });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const val = Math.max(0, Math.min(100, parseInt(marginDrafts[side]) || 0));
                      setMarginDrafts(prev => ({ ...prev, [side]: String(val) }));
                      onSetMargins?.({ ...coverMargins, [side]: val });
                    }
                  }}
                  style={{ width: 42, padding: '4px 4px', fontSize: 12, border: '1px solid #ccc', borderRadius: 4, textAlign: 'center' }}
                />
              </label>
            ))}
          </div>

          <div className="toolbar-divider" />

          {/* Size — select from offered book sizes */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Size</span>
            <select
              value={bookSize || BOOK_SIZES.find(s => s.w === localFaceW && s.h === localFaceH)?.key || ''}
              onChange={(e) => {
                const selected = BOOK_SIZES.find(s => s.key === e.target.value);
                if (!selected) return;
                setLocalFaceW(selected.w);
                setLocalFaceH(selected.h);
                onBookSizeChange?.(selected.key, { faceW: selected.w, faceH: selected.h });
                onResizeCover?.({ faceW: selected.w, faceH: selected.h, spineW: localSpineW });
              }}
              style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #ccc', borderRadius: 4, maxWidth: 180 }}
            >
              <option value="" disabled>Select size…</option>
              {BOOK_SIZES.map(s => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="toolbar-divider" />

          {/* Thickness */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Thickness</span>
            <select
              value={thicknessMode}
              onChange={(e) => setThicknessMode(e.target.value)}
              style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #ccc', borderRadius: 4 }}
            >
              <option value="pages">By Pages</option>
              <option value="manual">Manual</option>
            </select>
            {thicknessMode === 'pages' ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}>
                <input
                  type="text" inputMode="numeric"
                  value={pageCountDraft}
                  onChange={(e) => setPageCountDraft(e.target.value)}
                  onBlur={() => { if (skipNextBlurRef.current) { skipNextBlurRef.current = false; return; } commitPageCount(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { skipNextBlurRef.current = true; commitPageCount(); } }}
                  style={{ width: 52, padding: '4px 4px', fontSize: 12, border: '1px solid #ccc', borderRadius: 4, textAlign: 'center' }}
                />
                <span style={{ color: '#999', fontSize: 10 }}>pp → {localSpineW}px</span>
              </label>
            ) : (
              <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}>
                <input
                  type="text" inputMode="numeric"
                  value={spineWDraft}
                  onChange={(e) => setSpineWDraft(e.target.value)}
                  onBlur={() => { if (skipNextBlurRef.current) { skipNextBlurRef.current = false; return; } commitSpineW(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { skipNextBlurRef.current = true; commitSpineW(); } }}
                  style={{ width: 52, padding: '4px 4px', fontSize: 12, border: '1px solid #ccc', borderRadius: 4, textAlign: 'center' }}
                />
                <span style={{ color: '#999', fontSize: 10 }}>px</span>
              </label>
            )}
          </div>

          <div className="toolbar-divider" />

          {/* ISBN */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Back Cover</span>
            <button
              onClick={isbnOnCanvas ? onRemoveISBN : onAddISBN}
              disabled={activeView !== 'back' && activeView !== 'full'}
              className="primary-btn"
              title={activeView !== 'back' && activeView !== 'full'
                ? 'Switch to Back or Full view to add ISBN'
                : isbnOnCanvas ? 'Remove the ISBN barcode' : 'Place a sample ISBN barcode on the back cover'}
            >
              🔢 {isbnOnCanvas ? 'Remove ISBN' : 'Add ISBN'}
            </button>
            <button
              onClick={onAddBlurb}
              disabled={activeView !== 'back' && activeView !== 'full'}
              className="primary-btn"
              title={activeView !== 'back' && activeView !== 'full'
                ? 'Switch to Back or Full view to add Blurb'
                : draftBlurb ? 'Place your saved blurb on the back cover' : 'Place placeholder blurb text'}
            >
              📝 Add Blurb{!draftBlurb && ' (none saved)'}
            </button>
          </div>

          <div className="toolbar-divider" />

          {/* Download */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Export</span>
            <button className="primary-btn" onClick={onDownloadPDF} title="Download cover as PDF">
              ⬇ Download PDF
            </button>
          </div>
        </div>
      )}

      {/* ── TEXT panel: add text, font, style, align, orient, text colors ── */}
      {activePanel === 'text' && (
        <div className="toolbar-row text-row">
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Add</span>
            <button onClick={onAddText} className="primary-btn" title="Click canvas to place text">
              📝 Text
            </button>
          </div>

          <div className="toolbar-divider" />

          <div className="toolbar-group toolbar-section">
            <span className="section-label">Font</span>
            <div className="font-select-wrapper dropdown-wrapper">
              <button
                className="font-select-trigger"
                style={{ fontFamily: fontFamily || 'inherit' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => { e.stopPropagation(); setShowFontDropdown(f => !f); }}
                title="Font family"
              >
                <span className="font-select-label">{fontFamily || 'Mixed'}</span>
                <span className="font-select-arrow">▾</span>
              </button>
              {showFontDropdown && (
                <div className="font-select-menu dropdown-menu">
                  {FONTS.map((font) => (
                    <div
                      key={font}
                      className={`font-select-option${fontFamily === font ? ' active' : ''}`}
                      style={{ fontFamily: font }}
                      onMouseDown={(e) => { e.preventDefault(); setFontFamily(font); updateText('fontFamily', font); setShowFontDropdown(false); }}
                    >
                      {font}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <select
              value={fontSize}
              onChange={(e) => { if (!e.target.value) return; const size = parseInt(e.target.value, 10); setFontSize(size); updateText('fontSize', size); }}
              className="size-select"
            >
              {fontSize === '' && <option value="" disabled style={{ color: '#aaa' }}>—</option>}
              {FONT_SIZES.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <div className="toolbar-divider" />

          <div className="toolbar-group toolbar-section">
            <span className="section-label">Style</span>
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => updateText('fontWeight', textStyles.bold ? 'normal' : 'bold')}
              className={`style-btn ${textStyles.bold ? 'active' : ''}`}
              title="Bold"
            >
              <strong>B</strong>
            </button>
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => updateText('fontStyle', textStyles.italic ? 'normal' : 'italic')}
              className={`style-btn ${textStyles.italic ? 'active' : ''}`}
              title="Italic"
            >
              <em>I</em>
            </button>
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => updateText('underline', !textStyles.underline)}
              className={`style-btn ${textStyles.underline ? 'active' : ''}`}
              title="Underline"
            >
              <u>U</u>
            </button>
          </div>

          <div className="toolbar-divider" />

          <div className="toolbar-group toolbar-section">
            <span className="section-label">Align</span>
            <button onClick={() => { setTextAlign('left'); updateText('textAlign', 'left'); }} className={`style-btn ${(activeText?.textAlign ?? textAlign) === 'left' ? 'active' : ''}`} title="Align Left">☰</button>
            <button onClick={() => { setTextAlign('center'); updateText('textAlign', 'center'); }} className={`style-btn ${(activeText?.textAlign ?? textAlign) === 'center' ? 'active' : ''}`} title="Align Center">≡</button>
            <button onClick={() => { setTextAlign('right'); updateText('textAlign', 'right'); }} className={`style-btn ${(activeText?.textAlign ?? textAlign) === 'right' ? 'active' : ''}`} title="Align Right">☰</button>
          </div>

          <div className="toolbar-divider" />

          <div className="toolbar-group toolbar-section">
            <span className="section-label">Orient</span>
            <button
              onClick={() => { if (!activeText || !canvas) return; activeText.set({ angle: 0 }); canvas.requestRenderAll(); canvas.fire('object:modified', { target: activeText }); setActiveTextAngle(0); }}
              className={`style-btn ${activeTextAngle === 0 || activeTextAngle === 360 ? 'active' : ''}`}
              title="Horizontal" disabled={!activeText}
            >↔</button>
            <button
              onClick={() => { if (!activeText || !canvas) return; activeText.set({ angle: 90 }); canvas.requestRenderAll(); canvas.fire('object:modified', { target: activeText }); setActiveTextAngle(90); }}
              className={`style-btn ${activeTextAngle === 90 ? 'active' : ''}`}
              title="Top to Bottom" disabled={!activeText}
            >↓</button>
            <button
              onClick={() => { if (!activeText || !canvas) return; activeText.set({ angle: -90 }); canvas.requestRenderAll(); canvas.fire('object:modified', { target: activeText }); setActiveTextAngle(-90); }}
              className={`style-btn ${activeTextAngle === -90 || activeTextAngle === 270 ? 'active' : ''}`}
              title="Bottom to Top" disabled={!activeText}
            >↑</button>
          </div>

          <div className="toolbar-divider" />

          <div className="toolbar-group toolbar-section">
            <span className="section-label">Colors</span>
            <div className="dropdown-wrapper">
              <button onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); setShowTextPalette(!showTextPalette); }} title="Text Color">
                Text <span
                  className="color-box"
                  style={textColor === ''
                    ? { background: 'repeating-linear-gradient(45deg,#ccc 0px,#ccc 3px,#fff 3px,#fff 6px)', border: '1px solid #aaa' }
                    : { backgroundColor: textColor }
                  }
                />
              </button>
              {showTextPalette && (
                <div className="dropdown-menu color-grid">
                  {PALETTE.map((color) => (
                    <div key={color} className="color-swatch" style={{ backgroundColor: color }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setTextColor(color); updateText('fill', color); setShowTextPalette(false); }} />
                  ))}
                </div>
              )}
            </div>
            <div className="dropdown-wrapper">
              <button onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); setShowBgPalette(!showBgPalette); }} title="Text Background">
                Highlight <span className="color-box" style={{ backgroundColor: bgColor || 'transparent' }} />
              </button>
              {showBgPalette && (
                <div className="dropdown-menu color-grid">
                  {PALETTE.map((color) => (
                    <div key={color} className="color-swatch" style={{ backgroundColor: color }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setBgColor(color); updateText('textBackgroundColor', color); setShowBgPalette(false); }} />
                  ))}
                  <button onMouseDown={(e) => e.preventDefault()} className="transparent-btn" onClick={() => { setBgColor(''); updateText('textBackgroundColor', ''); setShowBgPalette(false); }}>No Background</button>
                </div>
              )}
            </div>
          </div>

          <div className="toolbar-divider" />

          <div className="toolbar-group toolbar-section">
            <span className="section-label">Spacing</span>
            <select
              value={activeText?.lineHeight ?? 1.16}
              onChange={(e) => { const v = parseFloat(e.target.value); updateText('lineHeight', v); }}
              disabled={!activeText}
              className="size-select"
              title="Line Spacing"
            >
              {[0.8, 1.0, 1.16, 1.5, 2.0, 2.5, 3.0].map((v) => (
                <option key={v} value={v}>{v}×</option>
              ))}
            </select>
          </div>

          <div className="toolbar-divider" />

          <div className="toolbar-group toolbar-section">
            <span className="section-label">Textbox</span>
            <div className="dropdown-wrapper">
              <button onClick={(e) => { e.stopPropagation(); setShowTbFillPalette(!showTbFillPalette); }} disabled={!activeText} title="Textbox Fill Color">
                Fill <span className="color-box" style={{ backgroundColor: activeText?.backgroundColor || 'transparent', border: '1px solid #ccc' }} />
              </button>
              {showTbFillPalette && (
                <div className="dropdown-menu color-grid">
                  {PALETTE.map((color) => (
                    <div key={color} className="color-swatch" style={{ backgroundColor: color }}
                      onClick={() => { if (activeText && canvas) { activeText.set('backgroundColor', color); canvas.requestRenderAll(); onCanvasChange?.(); } setShowTbFillPalette(false); }} />
                  ))}
                  <button className="transparent-btn" onClick={() => { if (activeText && canvas) { activeText.set('backgroundColor', ''); canvas.requestRenderAll(); onCanvasChange?.(); } setShowTbFillPalette(false); }}>No Fill</button>
                </div>
              )}
            </div>
            <div className="dropdown-wrapper">
              <button onClick={(e) => { e.stopPropagation(); setShowTbStrokePalette(!showTbStrokePalette); }} disabled={!activeText} title="Textbox Border Color">
                Border <span className="color-box" style={{ backgroundColor: activeText?._boxStroke || 'transparent', border: '1px solid #ccc' }} />
              </button>
              {showTbStrokePalette && (
                <div className="dropdown-menu color-grid">
                  {PALETTE.map((color) => (
                    <div key={color} className="color-swatch" style={{ backgroundColor: color }}
                      onClick={() => { if (activeText && canvas) { activeText._boxStroke = color; if (!activeText._boxStrokeWidth) activeText._boxStrokeWidth = 1; canvas.requestRenderAll(); onCanvasChange?.(); } setShowTbStrokePalette(false); }} />
                  ))}
                  <button className="transparent-btn" onClick={() => { if (activeText && canvas) { activeText._boxStroke = null; activeText._boxStrokeWidth = 0; canvas.requestRenderAll(); onCanvasChange?.(); } setShowTbStrokePalette(false); }}>No Border</button>
                </div>
              )}
            </div>
            <select
              value={activeText?._boxStrokeWidth ?? 0}
              onChange={(e) => { const w = parseInt(e.target.value, 10); if (activeText && canvas) { activeText._boxStrokeWidth = w; if (w > 0 && !activeText._boxStroke) activeText._boxStroke = '#000000'; canvas.requestRenderAll(); onCanvasChange?.(); } }}
              disabled={!activeText}
              className="size-select"
              title="Border Width"
              style={{ width: 52 }}
            >
              {[0, 1, 2, 3, 4, 5].map((w) => (
                <option key={w} value={w}>{w}px</option>
              ))}
            </select>
          </div>

          <div className="toolbar-divider" />

          {/* ── Text FX: Shadow · Glow · Reflection · Shade ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Text FX</span>

            {/* Shadow */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <div style={{ display: 'flex', gap: 1 }}>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={toggleTextShadow}
                  disabled={!activeText}
                  className={`style-btn${textShadowState.on ? ' active' : ''}`}
                  title="Text Shadow"
                >🌑</button>
                {textShadowState.on && (
                  <div className="dropdown-wrapper">
                    <button onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); setShowTextShadowPanel(!showTextShadowPanel); }} className="style-btn" title="Shadow Settings" style={{ fontSize: 11 }}>⚙</button>
                    {showTextShadowPanel && (
                      <div className="dropdown-menu" style={{ padding: 8, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 11 }}>Blur: {textShadowState.blur}
                          <input type="range" min="0" max="40" value={textShadowState.blur} onChange={(e) => updateTextShadow('blur', parseInt(e.target.value))} style={{ width: '100%' }} />
                        </label>
                        <label style={{ fontSize: 11 }}>Offset X: {textShadowState.offsetX}
                          <input type="range" min="-20" max="20" value={textShadowState.offsetX} onChange={(e) => updateTextShadow('offsetX', parseInt(e.target.value))} style={{ width: '100%' }} />
                        </label>
                        <label style={{ fontSize: 11 }}>Offset Y: {textShadowState.offsetY}
                          <input type="range" min="-20" max="20" value={textShadowState.offsetY} onChange={(e) => updateTextShadow('offsetY', parseInt(e.target.value))} style={{ width: '100%' }} />
                        </label>
                        <div style={{ fontSize: 11 }}>Color <span className="color-box" style={{ backgroundColor: textShadowState.color, border: '1px solid #ccc', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setShowTextShadowColorGrid(!showTextShadowColorGrid); }} /></div>
                        {showTextShadowColorGrid && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, width: 180 }}>
                            {PALETTE.map((color) => <div key={color} className="color-swatch" style={{ backgroundColor: color }} onClick={() => { updateTextShadow('color', color); setShowTextShadowColorGrid(false); }} />)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 9, color: '#888', lineHeight: 1 }}>Shadow</span>
            </div>

            {/* Glow */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <div style={{ display: 'flex', gap: 1 }}>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={toggleGlow}
                  disabled={!activeText}
                  className={`style-btn${glowState.on ? ' active' : ''}`}
                  title="Text Glow"
                >✨</button>
                {glowState.on && (
                  <div className="dropdown-wrapper">
                    <button onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); setShowGlowPanel(!showGlowPanel); }} className="style-btn" title="Glow Settings" style={{ fontSize: 11 }}>⚙</button>
                    {showGlowPanel && (
                      <div className="dropdown-menu" style={{ padding: 8, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 11 }}>Thickness: {glowState.thickness}
                          <input type="range" min="1" max="25" value={glowState.thickness} onChange={(e) => updateGlow('thickness', parseInt(e.target.value))} style={{ width: '100%' }} />
                        </label>
                        <label style={{ fontSize: 11 }}>Brightness: {glowState.brightness > 0 ? '+' : ''}{glowState.brightness}
                          <input type="range" min="-100" max="100" value={glowState.brightness} onChange={(e) => updateGlow('brightness', parseInt(e.target.value))} style={{ width: '100%' }} />
                        </label>
                        <div style={{ fontSize: 11 }}>Color <span className="color-box" style={{ backgroundColor: glowState.color, border: '1px solid #ccc', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setShowGlowColorGrid(!showGlowColorGrid); }} /></div>
                        {showGlowColorGrid && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, width: 180 }}>
                            {PALETTE.map((color) => <div key={color} className="color-swatch" style={{ backgroundColor: color }} onClick={() => { updateGlow('color', color); setShowGlowColorGrid(false); }} />)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 9, color: '#888', lineHeight: 1 }}>Glow</span>
            </div>

            {/* Reflection */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <div style={{ display: 'flex', gap: 1 }}>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={toggleReflection}
                  disabled={!activeText}
                  className={`style-btn${reflectionState.on ? ' active' : ''}`}
                  title="Text Reflection"
                >🪞</button>
                {reflectionState.on && (
                  <div className="dropdown-wrapper">
                    <button onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); setShowReflectionPanel(!showReflectionPanel); }} className="style-btn" title="Reflection Settings" style={{ fontSize: 11 }}>⚙</button>
                    {showReflectionPanel && (
                      <div className="dropdown-menu" style={{ padding: 8, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '42vh', overflowY: 'auto' }}>
                        <div style={{ fontSize: 11 }}>Direction
                          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                            {[['above','↑'],['below','↓'],['left','←'],['right','→']].map(([d, icon]) => (
                              <button key={d} onMouseDown={(e) => e.preventDefault()}
                                onClick={() => updateReflection('direction', d)}
                                className={`style-btn${reflectionState.direction === d ? ' active' : ''}`}
                                style={{ flex: 1, fontSize: 14, padding: '2px 0' }}
                                title={d.charAt(0).toUpperCase() + d.slice(1)}
                              >{icon}</button>
                            ))}
                          </div>
                        </div>
                        <label style={{ fontSize: 11 }}>Amount: {reflectionState.amount}%
                          <input type="range" min="0" max="100" value={reflectionState.amount} onChange={(e) => updateReflection('amount', parseInt(e.target.value))} style={{ width: '100%' }} />
                        </label>
                        <label style={{ fontSize: 11 }}>Size: {reflectionState.size}%
                          <input type="range" min="1" max="100" value={reflectionState.size} onChange={(e) => updateReflection('size', parseInt(e.target.value))} style={{ width: '100%' }} />
                        </label>
                        <label style={{ fontSize: 11 }}>Gap: {reflectionState.gap}px
                          <input type="range" min="0" max="50" value={reflectionState.gap} onChange={(e) => updateReflection('gap', parseInt(e.target.value))} style={{ width: '100%' }} />
                        </label>
                        <label style={{ fontSize: 11 }}>Blur: {reflectionState.blur}px
                          <input type="range" min="0" max="20" value={reflectionState.blur} onChange={(e) => updateReflection('blur', parseInt(e.target.value))} style={{ width: '100%' }} />
                        </label>
                        <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                          <input type="checkbox" checked={reflectionState.fade} onChange={(e) => updateReflection('fade', e.target.checked)} />
                          Fade out
                        </label>
                        <div style={{ fontSize: 11 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Brightness: {reflectionState.brightness > 0 ? '+' : ''}{reflectionState.brightness}</span>
                            {reflectionState.brightness !== 0 && (
                              <button onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); updateReflection('brightness', 0); }} style={{ fontSize: 10, padding: '0 4px', lineHeight: '14px', cursor: 'pointer' }} title="Reset to 0">↺</button>
                            )}
                          </div>
                          <input type="range" min="-100" max="100" value={reflectionState.brightness} onChange={(e) => updateReflection('brightness', parseInt(e.target.value))} style={{ width: '100%' }} />
                        </div>
                        <div style={{ fontSize: 11 }}>Color <span className="color-box"
                          style={reflectionState.color
                            ? { backgroundColor: reflectionState.color, border: '1px solid #ccc', cursor: 'pointer' }
                            : { background: 'repeating-linear-gradient(45deg,#ccc 0,#ccc 3px,#fff 3px,#fff 6px)', border: '1px solid #aaa', cursor: 'pointer' }}
                          onClick={(e) => { e.stopPropagation(); setShowReflectionColorGrid(!showReflectionColorGrid); }} />
                        </div>
                        {showReflectionColorGrid && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, width: 200 }}>
                            {PALETTE.map((color) => <div key={color} className="color-swatch" style={{ backgroundColor: color }} onClick={() => { updateReflection('color', color); setShowReflectionColorGrid(false); }} />)}
                            <button className="transparent-btn" onClick={() => { updateReflection('color', ''); setShowReflectionColorGrid(false); }}>Use Text Color</button>
                          </div>
                        )}

                        {/* ── Special Effects ────────────────────────────── */}
                        <div style={{ fontSize: 11, fontWeight: 600, marginTop: 4, borderTop: '1px solid #ddd', paddingTop: 4 }}>Special Effects</div>

                        {/* Rainbow */}
                        <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                          <input type="checkbox" checked={reflectionState.rainbow} onChange={(e) => updateReflection('rainbow', e.target.checked)} />
                          🌈 Rainbow
                        </label>

                        {/* Gradient (disabled when rainbow is on) */}
                        {!reflectionState.rainbow && (
                          <div style={{ fontSize: 11 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                              <input type="checkbox"
                                checked={!!(reflectionState.gradientColor1 || reflectionState.gradientColor2)}
                                onChange={(e) => {
                                  if (!e.target.checked) { updateReflection('gradientColor1', ''); updateReflection('gradientColor2', ''); }
                                  else { updateReflection('gradientColor1', reflectionState.gradientColor1 || '#ff0000'); updateReflection('gradientColor2', reflectionState.gradientColor2 || '#0000ff'); }
                                }} />
                              Gradient
                            </label>
                            {(reflectionState.gradientColor1 || reflectionState.gradientColor2) && (
                              <div style={{ display: 'flex', gap: 6, marginTop: 2, alignItems: 'center' }}>
                                <span>A</span>
                                <input type="color" value={reflectionState.gradientColor1 || '#ff0000'}
                                  onChange={(e) => updateReflection('gradientColor1', e.target.value)}
                                  style={{ width: 28, height: 20, padding: 0, border: 'none', cursor: 'pointer' }} />
                                <span>B</span>
                                <input type="color" value={reflectionState.gradientColor2 || '#0000ff'}
                                  onChange={(e) => updateReflection('gradientColor2', e.target.value)}
                                  style={{ width: 28, height: 20, padding: 0, border: 'none', cursor: 'pointer' }} />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Blend mode */}
                        <div style={{ fontSize: 11 }}>
                          Blend
                          <select value={reflectionState.blendMode} onChange={(e) => updateReflection('blendMode', e.target.value)}
                            style={{ marginLeft: 6, fontSize: 11 }}>
                            {[['source-over','Normal'],['multiply','Multiply'],['screen','Screen'],
                              ['overlay','Overlay'],['soft-light','Soft Light'],['hard-light','Hard Light'],
                              ['color-dodge','Color Dodge'],['color-burn','Color Burn'],
                              ['difference','Difference'],['exclusion','Exclusion']].map(([v,l]) => (
                              <option key={v} value={v}>{l}</option>
                            ))}
                          </select>
                        </div>

                        {/* Glitch / chromatic aberration */}
                        <label style={{ fontSize: 11 }}>Glitch: {reflectionState.glitch}px
                          <input type="range" min="0" max="20" value={reflectionState.glitch} onChange={(e) => updateReflection('glitch', parseInt(e.target.value))} style={{ width: '100%' }} />
                        </label>

                        {/* Wave distortion */}
                        <label style={{ fontSize: 11 }}>Wave: {reflectionState.wave}px
                          <input type="range" min="0" max="30" value={reflectionState.wave} onChange={(e) => updateReflection('wave', parseInt(e.target.value))} style={{ width: '100%' }} />
                        </label>
                        {reflectionState.wave > 0 && (
                          <label style={{ fontSize: 11 }}>Freq: {reflectionState.waveFreq}
                            <input type="range" min="1" max="10" step="0.5" value={reflectionState.waveFreq} onChange={(e) => updateReflection('waveFreq', parseFloat(e.target.value))} style={{ width: '100%' }} />
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 9, color: '#888', lineHeight: 1 }}>Reflect</span>
            </div>

            {/* Shade */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <div className="dropdown-wrapper">
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => { e.stopPropagation(); setShowTextShadePanel(!showTextShadePanel); }}
                  disabled={!activeText}
                  className={`style-btn${textShadeAmount !== 0 ? ' active' : ''}`}
                  title="Text Shade (light/dark)"
                >◐</button>
                {showTextShadePanel && (
                  <div className="dropdown-menu" style={{ padding: 8, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 6, right: 0, left: 'auto' }}>
                    <label style={{ fontSize: 11 }}>
                      {textShadeAmount > 0 ? `Lighter: +${textShadeAmount}` : textShadeAmount < 0 ? `Darker: ${textShadeAmount}` : 'Shade: 0'}
                      <input type="range" min="-100" max="100" value={textShadeAmount} onChange={(e) => updateTextShade(parseInt(e.target.value))} style={{ width: '100%' }} />
                    </label>
                    <button className="transparent-btn" onClick={() => updateTextShade(0)}>Reset</button>
                  </div>
                )}
              </div>
              <span style={{ fontSize: 9, color: '#888', lineHeight: 1 }}>Shade</span>
            </div>

          </div>
        </div>
      )}

      {/* ── OBJECT panel: history, object actions, fill/stroke, opacity ── */}
      {activePanel === 'object' && (
        <div className="toolbar-row" style={{ flexWrap: 'wrap', gap: 4 }}>
          {/* ── Insert ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Insert</span>
            <input type="file" ref={imageInputRef} accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
            <button onClick={() => imageInputRef.current?.click()} className="primary-btn" title="Upload an image">🖼️ Image</button>
            <div className="dropdown-wrapper">
              <button onClick={(e) => { e.stopPropagation(); setShowShapesMenu(!showShapesMenu); }} className="primary-btn">⬡ Shapes ▾</button>
              {showShapesMenu && (
                <div className="dropdown-menu shapes-menu">
                  <button onClick={() => addShape('rectangle')}>▢ Rectangle</button>
                  <button onClick={() => addShape('circle')}>○ Circle</button>
                  <button onClick={() => addShape('triangle')}>△ Triangle</button>
                  <button onClick={() => addShape('star')}>★ Star</button>
                  <button onClick={() => addShape('line')}>― Line</button>
                  <button onClick={() => addShape('arrow')}>→ Arrow</button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                type="number"
                min={1}
                max={99}
                value={borderSizeDraft}
                onChange={e => setBorderSizeDraft(e.target.value)}
                onBlur={() => {
                  const v = Math.max(1, Math.min(99, parseInt(borderSizeDraft) || borderSizePct));
                  setBorderSizePct(v);
                  setBorderSizeDraft(String(v));
                }}
                style={{ width: 48, textAlign: 'center' }}
                title="Border size (% of canvas)"
              />
              <span style={{ fontSize: 11, opacity: 0.7 }}>%</span>
              <button onClick={addBorder} className="primary-btn" title="Add decorative border">⬜ Border</button>
            </div>
          </div>

          <div className="toolbar-divider" />

          {/* ── History ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">History</span>
            <button onClick={undo} disabled={historyIndex <= 0} title="Undo (Ctrl+Z)" className="icon-btn">↶</button>
            <button onClick={redo} disabled={historyIndex >= history.length - 1} title="Redo (Ctrl+Y)" className="icon-btn">↷</button>
          </div>

          <div className="toolbar-divider" />

          {/* ── Object actions ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Object</span>
            <button onClick={duplicateSelected} disabled={!activeObject || activeObject?.name === 'isbn-barcode'} title="Duplicate (Ctrl+D)" className="icon-btn">📋</button>
            <button onClick={deleteSelected} disabled={!activeObject} title="Delete (Del)" className="icon-btn danger">🗑️</button>
            <button onClick={bringToFront} disabled={!activeObject} title="Bring to Front" className="icon-btn">⬆️</button>
            <button onClick={sendToBack} disabled={!activeObject} title="Send to Back" className="icon-btn">⬇️</button>
            {activeObject && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted, #888)', whiteSpace: 'nowrap' }}>Appears On</span>
                <select
                  value={(() => {
                    if (!activeObject.clipPath) return 'all';
                    const cp = activeObject.clipPath;
                    if ((cp.left || 0) <= 0) return 'back';
                    if (faceDims) {
                      const { faceW, spineW } = faceDims;
                      return (cp.left || 0) < faceW + spineW ? 'spine' : 'front';
                    }
                    // Fallback: spine is always much narrower than a face in every supported book size
                    return cp.width < cp.height * 0.3 ? 'spine' : 'front';
                  })()}
                  onChange={(e) => onSetObjectFace && onSetObjectFace(e.target.value)}
                  className="toolbar-select"
                  title="Choose which cover face(s) this object appears on"
                  style={{ fontSize: 11 }}
                >
                  <option value="all">Entire Cover</option>
                  <option value="front">Front Cover</option>
                  <option value="spine">Side Cover</option>
                  <option value="back">Back Cover</option>
                </select>
              </div>
            )}
          </div>

          <div className="toolbar-divider" />

          {/* ── Group / Ungroup ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Group</span>
            <button
              onClick={groupSelected}
              disabled={!(activeObject?.type === 'activeselection' && activeObject.getObjects().length >= 2)}
              className="group-btn group-btn--start"
              title="Group the selected objects"
            >
              🔗 Group
            </button>
            <button
              onClick={ungroupSelected}
              disabled={activeObject?.type !== 'group'}
              className="group-btn"
              title="Ungroup"
            >
              🔓 Ungroup
            </button>
            <span className="group-status">
              {activeObject?.type === 'activeselection' && activeObject.getObjects().length >= 2
                ? `${activeObject.getObjects().length} selected`
                : 'Select 2+ objects to group'}
            </span>
          </div>

          <div className="toolbar-divider" />

          {/* ── Colors & Border ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Colors</span>
            <div className="dropdown-wrapper">
              <button onClick={(e) => { e.stopPropagation(); setShowShapePalette(!showShapePalette); }} title="Fill Color">
                Fill <span className="color-box" style={{ backgroundColor: currentShapeFill }} />
              </button>
              {showShapePalette && (
                <div className="dropdown-menu color-grid">
                  {PALETTE.map((color) => (
                    <div key={color} className="color-swatch" style={{ backgroundColor: color }}
                      onClick={() => { updateShape('fill', color); setShowShapePalette(false); }} />
                  ))}
                  <button className="transparent-btn" onClick={() => { updateShape('fill', 'transparent'); setShowShapePalette(false); }}>Transparent</button>
                </div>
              )}
            </div>
            <div className="dropdown-wrapper">
              <button onClick={(e) => { e.stopPropagation(); setShowStrokePalette(!showStrokePalette); }} title="Stroke / Border Color">
                Stroke <span className="color-box" style={{ backgroundColor: currentStroke }} />
              </button>
              {showStrokePalette && (
                <div className="dropdown-menu color-grid">
                  {PALETTE.map((color) => (
                    <div key={color} className="color-swatch" style={{ backgroundColor: color }}
                      onClick={() => { updateStrokeColor(color); setShowStrokePalette(false); }} />
                  ))}
                  <button className="transparent-btn" onClick={() => { updateStrokeColor(null); setShowStrokePalette(false); }}>No Stroke</button>
                </div>
              )}
            </div>
            <div className="dropdown-wrapper">
              <button
                onClick={(e) => { e.stopPropagation(); setShowBorderPanel(!showBorderPanel); }}
                disabled={!activeObject}
                className="icon-btn"
                title="Border Style & Weight"
              >⚙️</button>
              {showBorderPanel && (
                <div className="dropdown-menu" style={{ padding: 8, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {BORDER_STYLE_OPTIONS.map((opt) => (
                      <button
                        key={opt.key}
                        className={`icon-btn${borderState.style === opt.key ? ' active' : ''}`}
                        onClick={() => updateBorderStyle(opt.key)}
                        title={opt.label}
                      >
                        {opt.key === 'none' ? '⊘' : (
                          <svg width="20" height="14" viewBox="0 0 20 14">
                            <line
                              x1="2" y1="7" x2="18" y2="7"
                              stroke="currentColor" strokeWidth="2"
                              strokeDasharray={BORDER_ICON_DASH[opt.key] || undefined}
                              strokeLinecap={opt.key === 'dotted' ? 'round' : 'butt'}
                            />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                  <label style={{ fontSize: 11 }}>Weight: {borderState.width}px
                    <input
                      type="range" min="0" max="20" value={borderState.width}
                      onChange={(e) => updateStrokeWidth(parseInt(e.target.value, 10))}
                      style={{ width: '100%' }}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="toolbar-divider" />

          {/* ── Crop (images only) ── */}
          {activeObject?.type === 'image' && (
            <>
              <div className="toolbar-group toolbar-section">
                <span className="section-label">Crop</span>
                <button onClick={cropImage} disabled={!activeObject} className="icon-btn" title="Crop image inward 10%">✂️ Crop</button>
                <button onClick={resetCrop} disabled={!activeObject} className="icon-btn" title="Reset crop">↩️ Reset</button>
              </div>
              <div className="toolbar-divider" />
            </>
          )}

          {/* ── Flip ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Flip</span>
            <button onClick={flipHorizontal} disabled={!activeObject} className="icon-btn" title="Flip Horizontal">↔️</button>
            <button onClick={flipVertical} disabled={!activeObject} className="icon-btn" title="Flip Vertical">↕️</button>
          </div>

          <div className="toolbar-divider" />

          {/* ── Rotate ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Rotate</span>
            <button onClick={() => rotateObject(-90)} disabled={!activeObject} className="icon-btn" title="Rotate 90° Left">↺</button>
            <button onClick={() => rotateObject(90)} disabled={!activeObject} className="icon-btn" title="Rotate 90° Right">↻</button>
            <button onClick={() => rotateObject(-15)} disabled={!activeObject} className="icon-btn" title="Rotate 15° Left">↰</button>
            <button onClick={() => rotateObject(15)} disabled={!activeObject} className="icon-btn" title="Rotate 15° Right">↱</button>
          </div>

          <div className="toolbar-divider" />

          {/* ── Lock ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Lock</span>
            <button
              onClick={toggleLock}
              disabled={!activeObject}
              className={`lock-btn${isObjectLocked ? ' active' : ''}`}
              title={isObjectLocked ? 'Unlock object to allow editing' : 'Lock object to prevent changes'}
            >
              {isObjectLocked ? '🔓 Unlock' : '🔒 Lock'}
            </button>
          </div>

          <div className="toolbar-divider" />

          {/* ── Transparency / Opacity ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Opacity</span>
            <input
              type="range" min="0" max="100" value={currentOpacity}
              onChange={(e) => updateOpacity(parseInt(e.target.value))}
              disabled={!activeObject} className="opacity-slider"
              title={`Opacity: ${currentOpacity}%`}
            />
            <span className="opacity-value">{currentOpacity}%</span>
          </div>

          <div className="toolbar-divider" />

          {/* ── Shade (color tint overlay) ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Shade</span>
            <div className="dropdown-wrapper">
              <button onClick={(e) => { e.stopPropagation(); setShowShadePalette(!showShadePalette); }} disabled={!activeObject} title="Apply color shade">
                Tint <span className="color-box" style={{ backgroundColor: activeObject?.fill || 'transparent' }} />
              </button>
              {showShadePalette && (
                <div className="dropdown-menu color-grid">
                  {[
                    'rgba(0,0,0,0.2)', 'rgba(0,0,0,0.4)', 'rgba(0,0,0,0.6)',
                    'rgba(255,255,255,0.2)', 'rgba(255,255,255,0.4)', 'rgba(255,255,255,0.6)',
                    'rgba(139,0,0,0.3)', 'rgba(0,0,139,0.3)', 'rgba(0,100,0,0.3)',
                    'rgba(184,134,11,0.3)', 'rgba(75,0,130,0.3)', 'rgba(0,128,128,0.3)',
                  ].map((color) => (
                    <div key={color} className="color-swatch" style={{ backgroundColor: color }}
                      onClick={() => { applyShade(color); setShowShadePalette(false); }} />
                  ))}
                  <button className="transparent-btn" onClick={() => { removeShade(); setShowShadePalette(false); }}>No Shade</button>
                </div>
              )}
            </div>
          </div>

          <div className="toolbar-divider" />

          {/* ── Shadow ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Shadow</span>
            <button
              onClick={toggleShadow}
              disabled={!activeObject}
              className={`icon-btn${shadowState.on ? ' active' : ''}`}
              title={shadowState.on ? 'Remove Shadow' : 'Add Shadow'}
            >
              {shadowState.on ? '🌑 On' : '🌑 Off'}
            </button>
            {shadowState.on && (
              <div className="dropdown-wrapper">
                <button onClick={(e) => { e.stopPropagation(); setShowShadowPanel(!showShadowPanel); }} className="icon-btn" title="Shadow Settings">⚙️</button>
                {showShadowPanel && (
                  <div className="dropdown-menu" style={{ padding: 8, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 11 }}>Blur: {shadowState.blur}
                      <input type="range" min="0" max="40" value={shadowState.blur}
                        onChange={(e) => updateShadow('blur', parseInt(e.target.value))} style={{ width: '100%' }} />
                    </label>
                    <label style={{ fontSize: 11 }}>Offset X: {shadowState.offsetX}
                      <input type="range" min="-20" max="20" value={shadowState.offsetX}
                        onChange={(e) => updateShadow('offsetX', parseInt(e.target.value))} style={{ width: '100%' }} />
                    </label>
                    <label style={{ fontSize: 11 }}>Offset Y: {shadowState.offsetY}
                      <input type="range" min="-20" max="20" value={shadowState.offsetY}
                        onChange={(e) => updateShadow('offsetY', parseInt(e.target.value))} style={{ width: '100%' }} />
                    </label>
                    <div style={{ fontSize: 11 }}>
                      Color <span className="color-box" style={{ backgroundColor: shadowState.color, border: '1px solid #ccc', cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); setShowShadowColorGrid(!showShadowColorGrid); }} />
                    </div>
                    {showShadowColorGrid && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, width: 180, marginTop: 4 }}>
                        {PALETTE.map((color) => (
                          <div key={color} className="color-swatch" style={{ backgroundColor: color }}
                            onClick={() => { updateShadow('color', color); setShowShadowColorGrid(false); }} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ DRAW panel ═══ */}
      {activePanel === 'draw' && (
        <div className="toolbar-row" style={{ flexWrap: 'wrap', gap: 4 }}>
          {/* ── Tool select ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Tool</span>
            {[
              { key: 'pencil', icon: '✏️', label: 'Pencil' },
              { key: 'paint',  icon: '🖌️', label: 'Paint' },
              { key: 'marker', icon: '🖍️', label: 'Marker' },
              { key: 'erase',  icon: '🧹', label: 'Erase' },
            ].map(t => (
              <button
                key={t.key}
                className={`draw-tool-btn${drawTool === t.key ? ' active' : ''}`}
                onClick={() => activateDrawTool(t.key)}
                title={t.key === 'erase' ? 'Erase drawn strokes' : t.label}
              >
                <span className="draw-icon">{t.icon}</span> {t.label}
              </button>
            ))}
            {/* Stroke / Area toggle — visible when Erase is active */}
            {drawTool === 'erase' && (
              <div className="erase-mode-toggle" style={{
                display: 'inline-flex', marginLeft: 6, borderRadius: 6,
                overflow: 'hidden', border: '1px solid #bbb', fontSize: 12,
              }}>
                <button
                  style={{
                    padding: '4px 10px', border: 'none', cursor: 'pointer',
                    fontWeight: 600, transition: 'all .15s',
                    background: eraseMode === 'stroke' ? '#4a90d9' : '#f0f0f0',
                    color: eraseMode === 'stroke' ? '#fff' : '#444',
                  }}
                  onClick={() => setEraseMode('stroke')}
                  title="Drag to remove entire strokes"
                >Stroke</button>
                <button
                  style={{
                    padding: '4px 10px', border: 'none', cursor: 'pointer',
                    fontWeight: 600, transition: 'all .15s',
                    background: eraseMode === 'area' ? '#4a90d9' : '#f0f0f0',
                    color: eraseMode === 'area' ? '#fff' : '#444',
                  }}
                  onClick={() => setEraseMode('area')}
                  title="Draw to erase parts of strokes"
                >Area</button>
              </div>
            )}
          </div>

          {(drawTool === 'pencil' || drawTool === null) && (
            <>
              <div className="toolbar-divider" />
              {/* ── Stroke Style — only for pencil; paint/marker have fixed behavior ── */}
              <div className="toolbar-group toolbar-section">
                <span className="section-label">Stroke</span>
                <div className="dropdown-wrapper">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowStrokeDropdown(v => !v); }}
                    title="Stroke style"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '3px 8px 3px 4px', borderRadius: 6,
                      border: '1px solid #bbb', background: '#fff',
                      cursor: 'pointer', fontSize: 12, fontWeight: 500,
                    }}
                  >
                    <StrokePreviewCanvas strokeKey={strokeStyle} />
                    {STROKE_STYLES.find(s => s.key === strokeStyle)?.label}
                    <span style={{ fontSize: 9, marginLeft: 2, color: '#888' }}>▾</span>
                  </button>
                  {showStrokeDropdown && (
                    <div
                      className="dropdown-menu"
                      style={{ minWidth: 170, padding: 4, display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      {STROKE_STYLES.map(s => (
                        <button
                          key={s.key}
                          onClick={() => { updateStrokeStyle(s.key); setShowStrokeDropdown(false); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '4px 6px', borderRadius: 5, border: 'none',
                            background: strokeStyle === s.key ? '#e8f0fd' : 'transparent',
                            cursor: 'pointer', textAlign: 'left', fontSize: 12,
                            fontWeight: strokeStyle === s.key ? 600 : 400,
                          }}
                        >
                          <StrokePreviewCanvas strokeKey={s.key} />
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="toolbar-divider" />

          {/* ── Width ── */}
          <div className="toolbar-group toolbar-section">
            <span className="section-label">Width</span>
            <input
              type="range" min="1" max="50" value={drawWidth}
              onChange={(e) => updateDrawWidth(parseInt(e.target.value))}
              className="opacity-slider" title={`Brush width: ${drawWidth}px`}
            />
            <span className="opacity-value">
              {strokeStyle === 'lighter-ink'
                ? Math.max(Math.round(drawWidth * 0.7), 1)
                : strokeStyle === 'light-running-ink'
                  ? Math.max(Math.round(drawWidth * 0.5), 1)
                  : drawWidth}px
            </span>
          </div>

          {drawTool !== 'erase' && (
            <>
              <div className="toolbar-divider" />

              {/* ── Color ── */}
              <div className="toolbar-group toolbar-section">
                <span className="section-label">Color</span>
                <div className="dropdown-wrapper">
                  <button onClick={(e) => { e.stopPropagation(); setShowDrawColorGrid(!showDrawColorGrid); }} title="Drawing Color">
                    <span className="color-box" style={{ backgroundColor: drawColor }} />
                  </button>
                  {showDrawColorGrid && (
                    <div className="dropdown-menu color-grid">
                      {PALETTE.map((color) => (
                        <div key={color} className="color-swatch" style={{ backgroundColor: color }}
                          onClick={() => { updateDrawColor(color); setShowDrawColorGrid(false); }} />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="toolbar-divider" />

              {/* ── Opacity ── */}
              <div className="toolbar-group toolbar-section">
                <span className="section-label">Opacity</span>
                <input
                  type="range" min="1" max="100" value={drawOpacity}
                  onChange={(e) => updateDrawOpacity(parseInt(e.target.value))}
                  className="opacity-slider" title={`Opacity: ${drawOpacity}%`}
                />
                <span className="opacity-value">{drawOpacity}%</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
});

export default TextStyleToolbar;
